'use client';

import React, { Ref, useEffect, useMemo, useRef, useState } from 'react';
import styles from '@/styles/item.module.scss';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/navbar';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import Lottie from 'lottie-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { RemoveScroll } from "react-remove-scroll";
import moment from 'moment-timezone';

import 'swiper/css';
import 'swiper/css/pagination';

import useStore from '@store/index';
import PreloaderComponent from '@/components/preloader';
import { ChevronDown, ChevronsDown, Minus, Plus, Star, X } from 'react-feather';
import { Button, Carousel, Container, Form } from 'react-bootstrap';
import { encryptData, formatCurrency, modifiersFilter, notesCharLimit, removeSpacesFromString } from '_services/common.service';
import CustomizeSelector from '../modals/customizeSelector';
import ModalWrapperSheet from '@/components/modals/cart-success-sheet';
import cartsuccessstyles from '@/components/modals/cart-success-sheet.module.scss'

import emptyItemsLottie from '@/assets/lotties/empty_cart.json';
import { alertService } from '_services/alert.service';
import { CommonResponse, submitCollection, submitSets } from '_services/submit.service';
import { ImageUrlObj, ItemObj, OrderDetails, PaidPayObj } from '../modals/order-details-sheet';
import { OrderServeDetails } from '@store/slices/orderActions';
import { CartDetails } from './cart';
import { loadingService } from '_services/loading.service';

import itemAdded from '@/assets/lotties/item_added.json';
import { BiCircle } from 'react-icons/bi';
import CustomTooltip from '../common/customtooltip';
import RichTextViewer from '../common/richtextviewer';
import { event } from '@lib/ga';
import { cookingLoadingService } from '_services/loadingCooking.service';
import RemoveCustomizeConfirmation from '../modals/remove-customize-confirmation';

export interface PriceObject {
    amount: number;
    currency: string;
}

export interface TotalPriceObject {
    amount: number;
    currency: string;
}

export interface ModifierOption {
    optionId: string;
    optionName: string;
    optionDisplayName: string;
    isApplied: boolean;
    optionPrice: PriceObject;
    isSoldOut: boolean;
    isDefaultSelected: boolean;
    optionDescription: string;
    optionUid: string;
    quantity: number;
    totalPrice: TotalPriceObject;
    optionImageUrl: { imageUrl: string, imageName: string, imageId: string };
    allowQuantities: boolean;
}

export interface Modifier {
    allowQuantities: boolean;
    modifierName: string;
    mofireOptions: ModifierOption[];
    maxSelection: number;
    minSelection: number;
    modifierDisplayName: string;
    modifierId: string;
    modifierDescription: string;
    selectionType: string;
    isApplied: boolean;
    maxAllowQty: number;
}

export interface SideDetails {
    sideModifier: Modifier | null;
    modifiers: Modifier[];
}

export interface Variation{
  variationId: string;
  variationName: string;
  variationPrice: PriceObject;
  totalPrice: TotalPriceObject;
  variationImageUrl: string;
  isApplied: boolean;
  isDefaultSelected: boolean;
  isSoldOut: boolean;
}

export interface BottomSheetProps {
  show: boolean;
  onHide: () => void;
  title: string;
  children: React.ReactNode;
}

interface ErrorQueueItem {
  type: 'soldOut' | 'minSelection';
  message: string;
  itemId: string;     
  modifierId?: string;  
  button?: {
    text: string;
    onClick: () => void;
    className: string;
  };
}

export type Swiper = {
  slideTo: (index: number, speed?: number) => void;
  slideNext: (speed?: number) => void;
  slidePrev: (speed?: number) => void;
  on: (event: string, handler: () => void) => void;
};

const BottomSheet = ({ show, onHide, title, children }: BottomSheetProps) => {
  const t = useTranslations("ItemView");

  return (<>
  <div className={show ? styles.bottomSheetBackdrop + ' ' + styles.showBottomSheetBackdrop : styles.bottomSheetBackdrop}></div>

  <div className={`${styles.bottomSheet} ${show ? styles.show : ''} `}>
    <div className={styles.sheetHeader}>
      <h2 className={styles.sheetTitle}>{title.substring(0,55) + (title?.length > 55? '...' : '')}</h2>
      <button className={styles.resetButton} id={`combo-bottom-sheet-reset-button`} onClick={onHide}>{t('RESET')}</button>
    </div>
    {children}
  </div>
</>)};

export default function ItemComponent({ 
  loadedItemDetails, 
  isHalfAndHalf, 
  isBottomSheet = false,
  toggleBottomSheet,
  setModifiers,
  isComboHalfAndHalf =false,
  onModifierChange,
  handleSlideChange,
  totalPriceInc,
  // modifiers = [],
}: { 
  loadedItemDetails: ItemObj | null, 
  isHalfAndHalf: boolean, 
  isBottomSheet: boolean,
  ItemNameForAlert?:boolean ;
  toggleBottomSheet: unknown | null,
  setModifiers?: (modifiers: Modifier[]) => void | null,
  isComboHalfAndHalf?: boolean,
  handleSlideChange?: (optionId: string, isHalfAndHalf: boolean, side: string) => void | null,
  onModifierChange?: (optionId: string, index: number, isQuantityChange: boolean, applyValue: unknown, isHalfAndHalf: boolean) => void | null,
  totalPriceInc?: string,
  isShowComboOption?: boolean,
  // modifiers?: Modifier[]
}) {
  const t = useTranslations();

  const [totalPrice, setTotalPrice] = useState<string>(formatCurrency(0));
  const [showSuccessSheet, setShowSuccessSheet] = useState(false);
  const router = useRouter();

  const [isItemDetailsLoaded, setIsItemDetailsLoaded] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemObj | null>(null);

  const { cart, addToCart, hasHydrated, identityToken, companyDetails } = useStore((state) => state);
  
  const [showCustomize, setShowCustomize] = useState(false);
  const [showingTabId, setShowingTabId] = useState<string>('');
  const [showLeftPizzaSheet, setShowLeftPizzaSheet] = useState(false);
  const [showRightPizzaSheet, setShowRightPizzaSheet] = useState(false);

  const [leftSideDetails, setLeftSideDetails] = useState<SideDetails | null>(null);
  const [rightSideDetails, setRightSideDetails] = useState<SideDetails | null>(null);
  const [customizeSide, setCustomizeSide] = useState<"leftSideDetails" | "rightSideDetails">("leftSideDetails");

  const [showOutOfStockSheet, setShowOutOfStockSheet] = useState(false);
  const [showScrollToCustomization, setShowScrollToCustomization] = useState(false);

  const [availableLeftOptionsMod, setAvailableLeftOptionsMod] = useState<ModifierOption[]>([]);
  const [availableRightOptionsMod, setAvailableRightOptionsMod] = useState<ModifierOption[]>([]);
  const [showRemoveCustomizeConfirmation, setShowRemoveCustomizeConfirmation] = useState(false);
  const [pauseNextStep, setPauseNextStep] = useState(false);
  const [saveOrderErrorQueue, setSaveOrderErrorQueue] = useState<ErrorQueueItem[]>([]);
  const [currentSaveOrderErrorIndex, setCurrentSaveOrderErrorIndex] = useState(0);
  const [validErrorCount, setValidErrorCount] = useState(0);
  // const [showValidationMsg, setShowValidationMsg] = useState(false);

  const [inputOnFocus, setInputOnFocus] = useState(false);

  // Add these at the component level (where your state is defined)
  const showCustomizeRef = useRef(showCustomize);

  // Store initial state to detect changes
  const initialLeftSideRef = useRef<SideDetails | null>(null);
  const initialRightSideRef = useRef<SideDetails | null>(null);
  const initialDietaryRef = useRef<Modifier[]>([]);

  // Track last applied half selection to avoid redundant slide updates
  const lastLeftAppliedRef = useRef<string | null>(null);
  const lastRightAppliedRef = useRef<string | null>(null);

  /* const starEnumObj: Record<number, string> = {
    1: 'WORST',
    2: 'NEGATIVE',
    3: 'NEUTRAL',
    4: 'POSITIVE',
    5: 'EXCELLENT'
  } */

  // const leftSwiperRef = useRef<unknown | null>(null);
  // const rightSwiperRef = useRef<unknown | null>(null);

  const leftSwiperRef = useRef<{ swiper: SwiperClass } | null>(null);
  const rightSwiperRef = useRef<{ swiper: SwiperClass } | null>(null);
  // console.log("totalPrice",totalPrice);
  // console.log("totalPriceInc",totalPriceInc);

  useEffect(() => {
    if (loadedItemDetails && !isComboHalfAndHalf) {
      const updateLoadedItem = structuredClone(loadedItemDetails) as ItemObj;

      if(loadedItemDetails?.isSoldOut){
        setShowOutOfStockSheet(true);
      }
  
      if(isHalfAndHalf && updateLoadedItem.modifiers && updateLoadedItem.modifiers.length > 0) {
        const leftSideItem: Modifier | null = modifiersFilter(updateLoadedItem.modifiers, ["half1"])?.[0] ?? null;
        let leftSideModifiers = modifiersFilter(updateLoadedItem.modifiers, ["half1customs"]) || [];
       
        let isShowSideSoldoutMsg = false;

        if (leftSideItem) {
          // Sort options list by out of stock elements at the end
          leftSideItem.mofireOptions.sort((a: ModifierOption, b: ModifierOption) => {
            if (a.isSoldOut && !b.isSoldOut) return 1;
            if (!a.isSoldOut && b.isSoldOut) return -1;
            return 0;
          });
  
          if(!updateLoadedItem.itemUId && !isComboHalfAndHalf){
            /* leftSideItem = {
                ...leftSideItem,
                mofireOptions: leftSideItem.mofireOptions.map((option: ModifierOption, optionIndex: number) => ({
                    ...option,
                    isApplied: (option.isDefaultSelected || false)
                }))
            }; */
  
            leftSideModifiers = leftSideModifiers.map((modifier: Modifier) => ({
                ...modifier,
                mofireOptions: modifier.mofireOptions.map((option: ModifierOption) => {
                  // const optionDisplayName = structuredClone(option.optionName);

                  return ({
                    ...option,
                    isApplied: (option.isDefaultSelected || false),
                    quantity: 1,
                    // optionName : option.optionName.split(' ').slice(1).join(' '),
                    // optionDisplayName : optionDisplayName
                })})
            })
          );
            leftSideItem.mofireOptions = leftSideItem.mofireOptions.map((option: ModifierOption) => {
              const optionDisplayName = structuredClone(option.optionName);

              return ({
              ...option,
              // optionName : option.optionName.split(' ').slice(1).join(' '),
              optionName: option.optionName.replace(/^\d+\)\s*/, ''),
              optionDisplayName : optionDisplayName
            })});

          } else{
            // If update item, Check if selected option sold out. If sold out, unselect it
            if(leftSideItem.mofireOptions.some((opt: ModifierOption) => opt.isApplied && opt.isSoldOut)){
              isShowSideSoldoutMsg = true;
            }

            leftSideItem.mofireOptions = leftSideItem.mofireOptions.map((option: ModifierOption) => {
              const optionDisplayName = structuredClone(option.optionName);
              return ({
              ...option,
              isApplied: option.isApplied && !option.isSoldOut,
              // optionName : option.optionName.split(' ').slice(1).join(' '),
              optionName: option.optionName.replace(/^\d+\)\s*/, ''),
              optionDisplayName : optionDisplayName
            })});
          }
        }
        setLeftSideDetails(structuredClone({
            sideModifier: leftSideItem,
            modifiers: leftSideModifiers
        }));
  
        const rightSideItem: Modifier | null = modifiersFilter(updateLoadedItem.modifiers, ["half2"])?.[0] ?? null;
        let rightSideModifiers = modifiersFilter(updateLoadedItem.modifiers, ["half2customs"]) || [];
        
        if (rightSideItem) {
          // Sort options list by out of stock elements at the end
          rightSideItem.mofireOptions.sort((a: ModifierOption, b: ModifierOption) => {
              if (a.isSoldOut && !b.isSoldOut) return 1;
              if (!a.isSoldOut && b.isSoldOut) return -1;
              return 0;
          });
  
          if(!updateLoadedItem.itemUId && !isComboHalfAndHalf){
              /* rightSideItem = {
                  ...rightSideItem,
                  mofireOptions: rightSideItem.mofireOptions.map((option: ModifierOption, optionIndex: number) => ({
                      ...option,
                      isApplied: (option.isDefaultSelected || false)
                  }))
              }; */
  
              rightSideModifiers = rightSideModifiers.map((modifier: Modifier) => ({
                  ...modifier,
                  mofireOptions: modifier.mofireOptions.map((option: ModifierOption) => ({
                      ...option,
                      isApplied: (option.isDefaultSelected || false),
                      quantity: 1,
                  }))
              }));
              rightSideItem.mofireOptions = rightSideItem.mofireOptions.map((option: ModifierOption) => {
                const optionDisplayName = structuredClone(option.optionName);
                
                return({
              ...option,
              // optionName : option.optionName.split(' ').slice(1).join(' '),
              optionName: option.optionName.replace(/^\d+\)\s*/, ''),
              optionDisplayName : optionDisplayName
            })});
  
          } else{
            // If update item, Check if selected option sold out. If sold out, unselect it
            if(rightSideItem.mofireOptions.some((opt: ModifierOption) => opt.isApplied && opt.isSoldOut)){
              isShowSideSoldoutMsg = true;
            }

            rightSideItem.mofireOptions = rightSideItem.mofireOptions.map((option: ModifierOption) => {
              const optionDisplayName = structuredClone(option.optionName);
              return ({
              ...option,
              isApplied: option.isApplied && !option.isSoldOut,
              // optionName : option.optionName.split(' ').slice(1).join(' '),
              optionName: option.optionName.replace(/^\d+\)\s*/, ''),
              optionDisplayName : optionDisplayName
            })});
          }
        }
  
        setRightSideDetails(structuredClone({
            sideModifier: rightSideItem,
            modifiers: rightSideModifiers
        }));

        if(isShowSideSoldoutMsg){
          alertService.warn(t('ItemView.SIDE_SOLD_OUT_MSG'), 5000, true);
        }

      }
  
      //set modifiers applied default for new item
      if(!isComboHalfAndHalf){
        if(updateLoadedItem && !updateLoadedItem.itemUId){
          if(updateLoadedItem?.modifiers && updateLoadedItem?.modifiers.length > 0) {
            updateLoadedItem.modifiers.forEach((modifier: Modifier) => {
              modifier.mofireOptions.forEach((option: ModifierOption) => {
                option.isApplied = (option?.isDefaultSelected || false);
                option.quantity = 1; // Reset quantity for new items
              });
            });
          }
          // Check default selected variation sold out status for new items
          if(updateLoadedItem?.variations && updateLoadedItem?.variations.length > 0) {
            updateLoadedItem.variations.forEach((variation: Variation) => {
              if(variation.isApplied && variation.isSoldOut){
                variation.isApplied = false;
              }
            });
          }
    
          // Reset quantity for new items
          updateLoadedItem.quantity = 1;
    
        } else{
          updateLoadedItem.notes = (updateLoadedItem?.note || '');

            if(updateLoadedItem?.modifiers && updateLoadedItem?.modifiers.length > 0) {
            updateLoadedItem.modifiers.forEach((modifier: Modifier) => {
              modifier.mofireOptions.forEach((option: ModifierOption) => {
                option.isApplied = (option?.isApplied && !option?.isSoldOut || false);
              });
            });
          }

        }
      }
      setSelectedItem(updateLoadedItem as ItemObj);
      setTotalPrice(formatCurrency((updateLoadedItem as ItemObj)?.price?.amount || 0));
    };

    setIsItemDetailsLoaded(true);

    loadingService.loading(false);
    // let currencyType = loadedItemDetails?.price.currency || 'USD';
    // console.log('Selected Item:', updateLoadedItem);
  }, [isHalfAndHalf, loadedItemDetails, isComboHalfAndHalf]);

  useEffect(()=>{
    if (loadedItemDetails && isComboHalfAndHalf) {
      const leftSideItem: Modifier | null = modifiersFilter(loadedItemDetails.modifiers, ["half1"])?.[0] ?? null;
      const leftSideModifiers = modifiersFilter(loadedItemDetails.modifiers, ["half1customs"]) || [];
      
      const updatedItem = structuredClone(loadedItemDetails) as ItemObj;
      
      // Remove old dietary modifiers
      updatedItem.modifiers = (updatedItem.modifiers || []).filter(
        (mod: Modifier) => !mod.modifierName.toLowerCase().includes('dietary')
      );
      
      // console.log("updatedItem with dietary modifiers for combo half and half",updatedItem);
      
      setSelectedItem(updatedItem);
    
      setLeftSideDetails({
        sideModifier: leftSideItem,
        modifiers: leftSideModifiers
      });
      if (leftSideItem) {
        leftSideItem.mofireOptions.sort((a: ModifierOption, b: ModifierOption) => {
          if (a.isSoldOut && !b.isSoldOut) return 1;
          if (!a.isSoldOut && b.isSoldOut) return -1;
          return 0;
        });
        leftSideItem.mofireOptions = leftSideItem.mofireOptions.map((option: ModifierOption) => {
          const optionDisplayName = structuredClone(option.optionName);
          
          return {
            ...option,
            optionName: option.optionName.replace(/^\d+\)\s*/, ''),
            optionDisplayName : optionDisplayName
          };
        });
      }
        

      const rightSideItem: Modifier | null = modifiersFilter(loadedItemDetails.modifiers, ["half2"])?.[0] ?? null;
      const rightSideModifiers = modifiersFilter(loadedItemDetails.modifiers, ["half2customs"]) || [];
      
      setRightSideDetails({
        sideModifier: rightSideItem,
        modifiers: rightSideModifiers
      });
      
      if (rightSideItem) {
        rightSideItem.mofireOptions.sort((a: ModifierOption, b: ModifierOption) => {
          if (a.isSoldOut && !b.isSoldOut) return 1;
          if (!a.isSoldOut && b.isSoldOut) return -1;
          return 0;
        });
        rightSideItem.mofireOptions = rightSideItem.mofireOptions.map((option: ModifierOption) => {
          const optionDisplayName = structuredClone(option.optionName);
          
          return {
            ...option,
            optionName: option.optionName.replace(/^\d+\)\s*/, ''),
            optionDisplayName : optionDisplayName
          };
        });
      }
    }
  },[loadedItemDetails])

  // Store initial state for change detection
  useEffect(() => {
    if (isHalfAndHalf && leftSideDetails && rightSideDetails && selectedItem) {
      if (!initialLeftSideRef.current) {
        initialLeftSideRef.current = structuredClone(leftSideDetails);
      }
      if (!initialRightSideRef.current) {
        initialRightSideRef.current = structuredClone(rightSideDetails);
      }
      if (initialDietaryRef.current.length === 0) {
        initialDietaryRef.current = structuredClone(modifiersFilter((selectedItem.modifiers || []), ['dietary']));
      }
    }
  }, [isHalfAndHalf, leftSideDetails, rightSideDetails, selectedItem]);

  const invalidModifiers = useMemo(() => {
    if (!selectedItem?.modifiers) return [];

    return selectedItem.modifiers.reduce<
      { modifierId: string; modifierName: string; minCount: number }[]
    >((acc, mod) => {
      if (mod.minSelection > 0) {
        const appliedCount =
          mod.mofireOptions?.filter((opt) => opt.isApplied === true).length ?? 0;

        if (appliedCount < mod.minSelection) {
          const name = mod.modifierName?.toLowerCase();
          let displayName = mod.modifierName;

          if (name?.includes("half 1")) {
            displayName = "Left Side Item";
          } else if (name?.includes("half 2")) {
            displayName = "Right Side Item";
          }

          acc.push({
            modifierId: mod.modifierId,
            modifierName: displayName,
            minCount: mod.minSelection,
          });
        }
      }
      return acc;
    }, []);
  }, [selectedItem]); 

  useEffect(() => {
    const totalsRecalculate = () => {
      // console.log('Recalculating totals for selected item:', selectedItem);
      
      if (!selectedItem) return;

      // let price = selectedItem.price?.amount || 0;
      let price =  0;
      // console.log('price :', price);

      // Apply size variation price
      const sizeVariation = selectedItem.variations?.find((v: Variation) => v.isApplied);
      if (sizeVariation && sizeVariation.variationPrice) {
        price += sizeVariation.variationPrice?.amount || 0;
      }

      if(isHalfAndHalf){
        // Apply half-and-half modifiers price

        leftSideDetails?.sideModifier?.mofireOptions?.forEach((option: ModifierOption) => {
          // console.log(option.isApplied, option.optionPrice);

          if (option.isApplied && option.optionPrice) {
            price += option.optionPrice?.amount * (parseInt(option.quantity.toString()) || 1);
          }
        });

        leftSideDetails?.modifiers?.forEach((modifier: Modifier) => {
          modifier.mofireOptions.forEach((option: ModifierOption) => {
            // console.log(option.isApplied, option.optionPrice);
            
            if (option.isApplied && option.optionPrice) {
              price += option.optionPrice?.amount * (parseInt(option.quantity.toString()) || 1);
            }
          });
        });

        rightSideDetails?.sideModifier?.mofireOptions?.forEach((option: ModifierOption) => {
          // console.log(option.isApplied, option.optionPrice);
          if (option.isApplied && option.optionPrice) {
            price += option.optionPrice?.amount * (parseInt(option.quantity.toString()) || 1);
          }
        });

        rightSideDetails?.modifiers?.forEach((modifier: Modifier) => {
          modifier.mofireOptions.forEach((option: ModifierOption) => {
            // console.log(option.isApplied, option.optionPrice);

            if (option.isApplied && option.optionPrice) {
              price += option.optionPrice?.amount * (parseInt(option.quantity.toString()) || 1);
            }
          });
        });

        modifiersFilter((selectedItem.modifiers || []), ['dietary']).forEach((modifier: Modifier) => {
          modifier.mofireOptions.forEach((option: ModifierOption) => {
            // console.log(option.isApplied, option.optionPrice);

            if (option.isApplied && option.optionPrice) {
              price += option.optionPrice?.amount * (parseInt(option.quantity.toString()) || 1);
            }
          });
        });
      } else{
        // Apply modifiers price
        selectedItem.modifiers?.forEach((modifier: Modifier) => {
          modifier.mofireOptions.forEach((option: ModifierOption) => {
            // console.log(option.isApplied, option.optionPrice);

            if (option.isApplied && option.optionPrice) {
              price += option.optionPrice?.amount * (parseInt(option.quantity.toString()) || 1);
            }
          });
        });
      }

      // console.log('Total Price after calculations:', price);

      const finalPrice = price * (selectedItem.quantity > 0 ? selectedItem.quantity : 1);
      setTotalPrice(formatCurrency(finalPrice , (selectedItem?.price?.currency || 'USD')));
    };

    totalsRecalculate();
  }, [selectedItem, isHalfAndHalf, leftSideDetails, rightSideDetails]);

  const defaultOrderDetails = () => ({
    orderId: "",
    orderVersion: 0,
    locationId: "",
    customerId: identityToken || "",
    lineItems: [] as ItemObj[],
    fulfillments: [] as OrderServeDetails[],
    payments: [] as PaidPayObj[],
    isUpdate: false,
    isDelete: false
  } as OrderDetails);

  const [orderDetails, setOrderDetails] = useState<OrderDetails>(defaultOrderDetails());

  useEffect(() => {
    if(!hasHydrated) return;

    const updatedOrderDetails = { ...orderDetails };
    // updatedOrderDetails.fulfillments = (orderServeDetails ? [orderServeDetails] : []);

    // Only update if different
    const isDifferent = JSON.stringify(orderDetails) !== JSON.stringify(updatedOrderDetails);
    if (isDifferent) {
      setOrderDetails(updatedOrderDetails);
    }

  }, [hasHydrated, orderDetails]);

  const handleSizeChange = (index: number) => {
    const updatedItemObj = { ...selectedItem } as ItemObj;
    if (updatedItemObj?.variations) {
      updatedItemObj.variations.forEach((variation: Variation) => {
        variation.isApplied = false;
      });
      updatedItemObj.variations[index].isApplied = true;
    }
    setSelectedItem(updatedItemObj);
  };

  const handleModifierChange = (optionId: string, index: number, isQuantityChange: boolean, applyValue: unknown, isSideUpdate: boolean = false) => {
    // console.log(optionId, index, isQuantityChange, applyValue, isSideUpdate);
    
    
    if(isSideUpdate){
      const sideDetails = (customizeSide === "leftSideDetails" ? leftSideDetails : rightSideDetails) as SideDetails;
      if(sideDetails) {
        sideDetails.modifiers.forEach((modifier: Modifier) => {
          if(modifier.modifierId === optionId) {
            // console.log('Found matching modifier:', modifier);

            if(isQuantityChange) {
              const option = modifier?.mofireOptions[index] || {};
              option.quantity = Math.max(0, Number((option?.quantity || 0)) + Number(applyValue));
              option.isApplied = option?.quantity > 0;
            } else{
              if(modifier.selectionType === "MULTIPLE") {
                const currentAppliedCount = modifier.mofireOptions.filter(opt => opt.isApplied).length;

                if(!(modifier.mofireOptions[index].isApplied) && modifier.maxSelection > 0 && currentAppliedCount >= modifier.maxSelection){
                  alertService.warn(t('ItemView.MODIFIER_SELECTION_RESTRICT_NOTICE', { max: modifier.maxSelection }), 3500, true);
                  return;
                }

                modifier.mofireOptions[index].isApplied = !modifier.mofireOptions[index].isApplied;
                // modifier.mofireOptions[index].quantity = modifier.mofireOptions[index].isApplied ? (parseFloat(modifier.minSelection.toString()) > 0 ? modifier.minSelection : 1) : 0;

              } else{
                modifier.mofireOptions.forEach((opt: ModifierOption) => {
                  opt.isApplied = false;
                  opt.quantity = 1;
                });

                modifier.mofireOptions[index].isApplied = true;
                // modifier.mofireOptions[index].quantity = (parseFloat(modifier.minSelection.toString()) > 0 ? modifier.minSelection : 1);
              }
            }
          }
        });

        if(customizeSide === "leftSideDetails") {
          setLeftSideDetails((prevState): SideDetails => ({
            ...prevState,
            modifiers: sideDetails.modifiers,
            sideModifier: prevState?.sideModifier ?? null
          }));
        } else {
          setRightSideDetails((prevState): SideDetails => ({
            ...prevState,
            modifiers: sideDetails.modifiers,
            sideModifier: prevState?.sideModifier ?? null
          }));
        }
      }

    } else{
      const updatedItemObj = { ...selectedItem } as ItemObj;
      const modifierIdx = updatedItemObj?.modifiers?.findIndex((mod: Modifier) => mod.modifierId === optionId) ?? -1;
  
      if(isQuantityChange) {
        if (modifierIdx !== -1) {
          const option = updatedItemObj?.modifiers?.[modifierIdx]?.mofireOptions[index] || {};
          option.quantity = Math.max(0, Number((option?.quantity || 0)) + Number(applyValue));
          option.isApplied = option?.quantity > 0;
        }
  
      } else{
        if (modifierIdx !== -1) {
          if(updatedItemObj?.modifiers?.[modifierIdx].selectionType === "MULTIPLE") {
            const currentAppliedCount = updatedItemObj.modifiers[modifierIdx].mofireOptions.filter(opt => opt.isApplied).length;

            if(!(updatedItemObj.modifiers[modifierIdx].mofireOptions[index].isApplied) && updatedItemObj.modifiers[modifierIdx].maxSelection > 0 && currentAppliedCount >= updatedItemObj.modifiers[modifierIdx].maxSelection){
              alertService.warn(t('ItemView.MODIFIER_SELECTION_RESTRICT_NOTICE', { max: updatedItemObj.modifiers[modifierIdx].maxSelection }), 3500, true);
              return;
            }

            updatedItemObj.modifiers[modifierIdx].mofireOptions[index].isApplied = ((applyValue as boolean) ? true : false);
          } else{
            updatedItemObj.modifiers[modifierIdx].mofireOptions.forEach((opt: ModifierOption) => {
              opt.isApplied = false;
            });
            updatedItemObj.modifiers[modifierIdx].mofireOptions[index].isApplied = ((applyValue as boolean) ? true : false);
          }
        }
  
      }
  
      setSelectedItem(updatedItemObj);
    }
  };

  const handleQuantityChange = (change: number) => {
    const updatedItemObj = { ...selectedItem } as ItemObj;
    const newQuantity = Math.max(1, parseInt(updatedItemObj.quantity.toString() || '1') + change);

    updatedItemObj['quantity'] = newQuantity;
    setSelectedItem(updatedItemObj);
  };

  /* const toggleHalfHalf = () => {
    // Logic to toggle half and half option
    console.log('Toggled Half & Half');
    window.location.href = '/halfhalf';
  }; */

  // Add this flag before your loop starts
  // let isDetected = false;

  const isModifierStillInvalid = (_itemUniqueId: string, modifierId: string): boolean => {
    const modifiers = isHalfAndHalf
      ? [
          ...(leftSideDetails?.modifiers ?? []),
          ...(rightSideDetails?.modifiers ?? []),
        ]
      : (selectedItem?.modifiers ?? []);

    const mod = modifiers.find(m => m.modifierId.toString() === modifierId.toString());
    if (!mod) return false;

    const appliedCount = mod.mofireOptions.filter((opt: ModifierOption) => opt.isApplied).length;
    return appliedCount < (mod.minSelection || 0);
  };


   
  // Update the ref whenever showCustomize changes
  useEffect(() => {
    showCustomizeRef.current = showCustomize;
  }, [showCustomize]);

  // Helper function to process save order errors one by one
  const showSaveOrderErrorsSequentially = (errors: ErrorQueueItem[]) => {
    if (errors.length === 0) return;

    setSaveOrderErrorQueue(errors);
    setCurrentSaveOrderErrorIndex(0);

    if(isHalfAndHalf){
      setValidErrorCount(errors.length); 
    }

    // Show first error
    const firstError = errors[0];
    if (firstError.button) {
      firstError.button.onClick();
    } else {
      // For errors without buttons (like sold out), just show alert
      alertService.error(firstError.message, 4500, true);
    }
  };

  useEffect(() => {
  if (!showCustomize && !pauseNextStep && saveOrderErrorQueue.length > 0 && currentSaveOrderErrorIndex < saveOrderErrorQueue.length) {
    let nextIndex = currentSaveOrderErrorIndex + 1; 
    // let skippedCount = 0;

    while (nextIndex < saveOrderErrorQueue.length) {
      const candidate = saveOrderErrorQueue[nextIndex];
      const stillInvalid = isModifierStillInvalid(candidate.itemId, candidate.modifierId || '');

      if (stillInvalid) {
        break;
      }
      // skippedCount++;
      nextIndex++;
    }

    if (nextIndex < saveOrderErrorQueue.length) {

      // if (skippedCount > 0) {
      //   setValidErrorCount(prev => prev - skippedCount);
      // }

      setTimeout(() => {
        setCurrentSaveOrderErrorIndex(nextIndex);
        const nextError = saveOrderErrorQueue[nextIndex];

        if (nextError.button) {
          nextError.button.onClick();
        } else {
          alertService.error(nextError.message, 4500, true);
        }
      }, 300);
    } else {
      setSaveOrderErrorQueue([]);
      setCurrentSaveOrderErrorIndex(0);
      setValidErrorCount(0);
    }
  }
}, [showCustomize, saveOrderErrorQueue, currentSaveOrderErrorIndex, pauseNextStep]);

  const saveOrder = () => {
    if (!orderDetails) return;
    setShowingTabId("");

    if(isHalfAndHalf){
      //validate half and half selection
      const leftSelected = leftSideDetails?.sideModifier?.mofireOptions.some((opt: ModifierOption) => opt.isApplied) || false;
      const rightSelected = rightSideDetails?.sideModifier?.mofireOptions.some((opt: ModifierOption) => opt.isApplied) || false;

      if (!leftSelected || !rightSelected) {
        alertService.error(t('ItemView.SELECT_OPTION'), 3000, true);
        return;
      }
    }

    if (selectedItem?.variations.filter((v: Variation) => v.isApplied).length === 0) {
      alertService.error(t('ItemView.SELECT_SIZE'), 3000, true);
      return;
    }

    const updatedOrderDetails = { ...orderDetails } as OrderDetails;
    
    if(cart && cart.orderId && cart.orderId !== ""){
      updatedOrderDetails.orderId = (cart?.orderId || "");
      updatedOrderDetails.orderVersion = (cart?.orderVersion || 0);
      updatedOrderDetails.locationId = (cart?.locationId || "");
      updatedOrderDetails.isDelete = false;
      updatedOrderDetails.isUpdate = true;
      updatedOrderDetails.fulfillments = [];
    } else{
      updatedOrderDetails.locationId = (companyDetails?.locationId || "");
    }

    updatedOrderDetails.customerId = identityToken || "";
    updatedOrderDetails.customerTimezone = moment.tz.guess() || "UTC";

    if(selectedItem?.variations.filter((v: Variation) => v.isApplied).length === 0) {
      alertService.error(t('ItemView.SELECT_SIZE'), 3000, true);
      return;
    }

    const savingSelectedItem = { ...selectedItem } as ItemObj;

    // Ensure required fields are not undefined and always strings (not undefined)
    savingSelectedItem.itemUId = savingSelectedItem.itemUId ?? null;
    savingSelectedItem.itemId = savingSelectedItem.itemId ?? '';
    savingSelectedItem.itemName = savingSelectedItem.itemName ?? '';
    savingSelectedItem.itemDescription = savingSelectedItem.itemDescription ?? '';
    savingSelectedItem.imageUrls = savingSelectedItem.imageUrls ?? [];
    savingSelectedItem.variations = savingSelectedItem.variations ?? [];
    savingSelectedItem.modifiers = savingSelectedItem.modifiers ?? [];
    savingSelectedItem.notes = savingSelectedItem.notes ?? '';
    savingSelectedItem.price = savingSelectedItem.price ?? { amount: 0, currency: 'USD' };
    savingSelectedItem.rating = savingSelectedItem.rating ?? { itemRating: '', totalReviewCount: 0 };
    savingSelectedItem.quantity = typeof savingSelectedItem.quantity === 'number' ? savingSelectedItem.quantity : 1;

    savingSelectedItem.isDelete = false;
    savingSelectedItem.isUpdate = (selectedItem?.itemUId ? true : false);
    savingSelectedItem.quantity = (selectedItem?.quantity || 1) > 0 ? (selectedItem?.quantity || 1) : 1;
    savingSelectedItem.variations = savingSelectedItem?.variations?.filter((v: Variation) => v.isApplied) || [];

    // let isShowMinSelectionNotice = false;
    // let isShowSoldOutModifierNotice = false;

    // Collect all errors instead of showing them immediately
    const errors: ErrorQueueItem[] = [];

    savingSelectedItem.modifiers = savingSelectedItem?.modifiers?.map((modifier: Modifier) => {

      const isSelectedOptionsSoldOut = modifier.mofireOptions.some((opt: ModifierOption) => opt.isApplied && opt.isSoldOut);
      if(isSelectedOptionsSoldOut){
        alertService.error(t('ItemView.SELECTED_MODIFIER_SOLD_OUT_NOTICE', { modiferName: modifier.modifierName }), 4500, true);
      }
      

      if(isHalfAndHalf && (modifier.modifierName.toLowerCase().includes("half 1") || modifier.modifierName.toLowerCase().includes("half 2"))){
        //Set selected half and half option
        const sideDetails = (modifier.modifierName.toLowerCase().includes("half 1") ? leftSideDetails : rightSideDetails) as SideDetails;
        if(sideDetails && sideDetails.sideModifier) {
          modifier = {
            ...modifier,
            mofireOptions: sideDetails.sideModifier.mofireOptions.map((option: ModifierOption) => ({
              ...option,
              isApplied: option.isApplied
            }))
          };
        }
      }

      if(isHalfAndHalf && modifier.modifierName.toLowerCase().includes("pizza 1")){
        //Find modifier from left side details
        const modifierIdx = leftSideDetails?.modifiers.findIndex((mod: Modifier) => mod.modifierId === modifier.modifierId) ?? -1;
        if(modifierIdx !== -1) {
            modifier = {
              ...modifier,
              mofireOptions: leftSideDetails?.modifiers[modifierIdx].mofireOptions.map((option: ModifierOption) => ({
                ...option,
                isApplied: option.isApplied
            })) || modifier.mofireOptions
          };
        }
      }

      if(isHalfAndHalf && modifier.modifierName.toLowerCase().includes("pizza 2")) {
        //Find modifier from right side details
        const modifierIdx = rightSideDetails?.modifiers.findIndex((mod: Modifier) => mod.modifierId === modifier.modifierId) ?? -1;
        if(modifierIdx !== -1) {
            modifier = {
              ...modifier,
              mofireOptions: rightSideDetails?.modifiers[modifierIdx].mofireOptions.map((option: ModifierOption) => ({
                ...option,
                isApplied: option.isApplied
            })) || modifier.mofireOptions
          };
        }
      }

      // Collect minimum selection errors
      if(modifier.minSelection > 0){
        const appliedCount = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied).length;

        if(appliedCount < modifier.minSelection){
          // Calculate sideDetails ONCE here and capture it in the closure
          // Calculate sideDetails and capture in closure
          const sideDetails = modifier.modifierName.toLowerCase().includes("pizza 1") 
            ? "leftSideDetails" 
            : "rightSideDetails";

          const currentModifierId = modifier.modifierId;
          const currentModifierName = modifier.modifierName;
          const currentMinSelection = modifier.minSelection;
          errors.push({
            type: 'minSelection',
            message: t('ItemView.MODIFIER_MIN_SELECTION_NOTICE', { 
              modifier: currentModifierName, 
              min: currentMinSelection 
            }),
            itemId: savingSelectedItem.itemId ?? '', 
            modifierId: currentModifierId, 
            button: {
              text: `Add ${currentModifierName}`,
              onClick: () => {
                setCustomizeSide(sideDetails);
                setShowingTabId(currentModifierId);
                handleShowCustomize(true, sideDetails);
              },
              className: 'custom-alert-button'
            }
          });
        }
      }

      return {
        ...modifier,
        mofireOptions: modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied)
      };
    }) || [];

    // Show all errors sequentially
    if(errors.length > 0){
      if(isHalfAndHalf){
        setValidErrorCount(errors.length); 
      }
      showSaveOrderErrorsSequentially(errors);
      return;
    }

    const findItemIndex = updatedOrderDetails.lineItems.findIndex((item: ItemObj) => item.itemId === selectedItem?.itemId);
    if (findItemIndex !== -1) {
      updatedOrderDetails.lineItems[findItemIndex] = savingSelectedItem;
    } else {
      updatedOrderDetails.lineItems.push(savingSelectedItem);
    }

    loadingService.loading(true);
    submitSets(submitCollection.saveOrder, updatedOrderDetails, false, null, false).then((res: CommonResponse) => {
      
      if (res && res.status) {
        if(res.extra){

          if(loadedItemDetails && loadedItemDetails.itemUId){
            alertService.success(t('ItemView.UPDATE_ITEM_SUCCESS'), 3000, true);
          }

          loadCart(res.extra as CartDetails, (loadedItemDetails && !loadedItemDetails.itemUId ? true : false));
        }

      } else {
        if(res && res.extra=== "Maximum allowed quantity for this item is 10"){
          loadingService.loading(false);
          alertService.error(t('ItemView.MAX_LIMIT_REACHED', { maxLimit: (selectedItem?.orderItemMaxQuantity || 1) }), 3000, true);
        }else{
          loadingService.loading(false);
          alertService.error(t('ItemView.ADD_TO_CART_FAILED'), 3000, true);
        }

        event({
          action: 'add_to_cart_failed',
          category: 'error',
          label: `Add to Cart Failed: ${(res && res.message) || 'Unknown error'}`
        });

      }
    }).catch((error: ErrorOptions) => {
      loadingService.loading(false);
      console.error('Error saving order:', error);
      alertService.error(t('ItemView.SAVE_ORDER_FAILED'), 3000, true);
    });

  };

  // const CloseHalfAndHalfSelection = () => {
  //   // Check if left side has changed
  //   const hasLeftSideChanged = () => {
  //     if (!initialLeftSideRef.current || !leftSideDetails) return false;
      
  //     const initialOption = initialLeftSideRef.current.sideModifier?.mofireOptions.find(opt => opt.isApplied)?.optionId;
  //     const currentOption = leftSideDetails.sideModifier?.mofireOptions.find(opt => opt.isApplied)?.optionId;
      
  //     if (initialOption !== currentOption) return true;
      
  //     // Check if modifiers have changed
  //     return JSON.stringify(initialLeftSideRef.current.modifiers) !== JSON.stringify(leftSideDetails.modifiers);
  //   };

  //   // Check if right side has changed
  //   const hasRightSideChanged = () => {
  //     if (!initialRightSideRef.current || !rightSideDetails) return false;
      
  //     const initialOption = initialRightSideRef.current.sideModifier?.mofireOptions.find(opt => opt.isApplied)?.optionId;
  //     const currentOption = rightSideDetails.sideModifier?.mofireOptions.find(opt => opt.isApplied)?.optionId;
      
  //     if (initialOption !== currentOption) return true;
  //     return JSON.stringify(initialRightSideRef.current.modifiers) !== JSON.stringify(rightSideDetails.modifiers);
  //   };

  //   const hasDietaryChanged = () => {
  //     if (!selectedItem) return false;
      
  //     const currentDietary = modifiersFilter((selectedItem.modifiers || []), ['dietary']);
  //     return JSON.stringify(initialDietaryRef.current) !== JSON.stringify(currentDietary);
  //   };

  //   const leftChanged = hasLeftSideChanged();
  //   const rightChanged = hasRightSideChanged();
  //   const dietaryChanged = hasDietaryChanged();

  //   if (leftChanged || rightChanged || dietaryChanged) {
  //     setShowRemoveCustomizeConfirmation(true);
  //   } else {
  //     (toggleBottomSheet as () => void)();
  //   }
  // }

  // const updateSelection = () => {
  //   // Revert changes back to initial state
  //   if (initialLeftSideRef.current) {
  //     setLeftSideDetails(structuredClone(initialLeftSideRef.current));
  //   }
    
  //   if (initialRightSideRef.current) {
  //     setRightSideDetails(structuredClone(initialRightSideRef.current));
  //   }
    
  //   if (initialDietaryRef.current.length > 0 && selectedItem) {
  //     const updatedItem = { ...selectedItem } as ItemObj;
  //     const dietaryModifiers = modifiersFilter((updatedItem.modifiers || []), ['dietary']);
      
  //     // Restore dietary options to initial state
  //     dietaryModifiers.forEach((currentModifier: Modifier) => {
  //       const initialModifier = initialDietaryRef.current.find(
  //         (mod: Modifier) => mod.modifierId === currentModifier.modifierId
  //       );
        
  //       if (initialModifier) {
  //         currentModifier.mofireOptions.forEach((option: ModifierOption) => {
  //           const initialOption = initialModifier.mofireOptions.find(
  //             (opt: ModifierOption) => opt.optionId === option.optionId
  //           );
  //           if (initialOption) {
  //             option.isApplied = initialOption.isApplied;
  //             option.quantity = initialOption.quantity;
  //           }
  //         });
  //       }
  //     });
      
  //     setSelectedItem(updatedItem);
  //   }
  //   setShowRemoveCustomizeConfirmation(false)
  // };

  const handleResetModifiers = () => {
    
    if(isHalfAndHalf){
      const sideDetails = (customizeSide === "leftSideDetails" ? leftSideDetails : rightSideDetails) as SideDetails;

      if(sideDetails) {
        modifiersFilter((sideDetails.modifiers || []), ['notdietary']).forEach((modifier: Modifier) => {
          modifier.mofireOptions.forEach((option: ModifierOption) => {
            option.isApplied = (option?.isDefaultSelected || false);
            option.quantity = 0;
          });
        });

        if(customizeSide === "leftSideDetails") {
          setLeftSideDetails((prevState): SideDetails => ({
            ...prevState,
            modifiers: sideDetails.modifiers,
            sideModifier: prevState?.sideModifier ?? null
          }));
        } else {
          setRightSideDetails((prevState): SideDetails => ({
            ...prevState,
            modifiers: sideDetails.modifiers,
            sideModifier: prevState?.sideModifier ?? null
          }));
        }
      }

    } else{
      if (!selectedItem) return;
  
      const updatedItemObj = { ...selectedItem };
      modifiersFilter((updatedItemObj.modifiers || []), ['notdietary']).forEach((modifier: Modifier) => {
        modifier.mofireOptions.forEach((option: ModifierOption) => {
          option.isApplied = (option?.isDefaultSelected || false);
          option.quantity = 0;
        });
      });
  
      setSelectedItem(updatedItemObj);
    }

  }

  const loadCart = async (cartDetails: CartDetails, isNew: boolean = false) => {
    const cartRes = await submitSets(submitCollection.viewCartOrder, {
      orderId: cartDetails?.orderId || ""
    }, true, {});
    
    loadingService.loading(false);
    if (cartRes && cartRes.status && cartRes.extra) {
      addToCart(cartRes.extra);

      if (isNew) {
        setShowSuccessSheet(true);
      } else{
        cookingLoadingService.loading(true);
        setTimeout(() => {
          const cardId = (cartRes.extra && cartRes.extra.orderId && cartRes.extra.orderId !== "" ? "?c=" + encryptData(cartRes.extra.orderId) : "");

          router.push('/cart' + cardId);
        }, 800);
      }
    } else {
      alertService.error(t('ItemView.LOAD_CART_FAILED'), 3000, true);
    }
  };

  const handleShowCustomize = (show: boolean, side: "leftSideDetails" | "rightSideDetails") => {
      setShowCustomize(show);
      setCustomizeSide(side);
  }

  /* const handleNextToggle = (side: "leftSideDetails" | "rightSideDetails") => {
      // Logic to handle next toggle
      setShowRightPizzaSheet(false);

      setShowCustomize(true);
      setCustomizeSide(side);
  } */

  const toggleHalfHalfSelect = (isLeft: boolean) => {
        if (isLeft) {
            setShowLeftPizzaSheet(true);
            setShowRightPizzaSheet(false);
        } else {
            setShowLeftPizzaSheet(false);
            setShowRightPizzaSheet(true);
        }
  }

  const getCustomizeCount = (isSideCustomize: boolean = false, side: string | null = null, isNumberReturn: boolean = false) => {
    let totalCount = 0;

    //Calculate total selected modifiers count
    if (isSideCustomize && side) {
      // Half and Half side customize count

      const sideModifiers = (side === "left" ? leftSideDetails?.modifiers : rightSideDetails?.modifiers) || [];

      //Add-ons count
      totalCount += modifiersFilter(sideModifiers, ['add']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied).length || 0);
      }, 0) || 0;

      //Ingredient count
      totalCount += modifiersFilter(sideModifiers, ['ingredient']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => !option.isApplied).length || 0);
      }, 0) || 0;

      //Removals count
      totalCount += modifiersFilter(sideModifiers, ['removal']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied).length || 0);
      }, 0) || 0;

      //Check base selection
      totalCount += modifiersFilter(sideModifiers, ['base']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied && !option.isDefaultSelected).length || 0);
      }, 0) || 0;

    } else{
      // Regular item customize count

      //Add-ons count
      totalCount += modifiersFilter((selectedItem?.modifiers || []), ['add']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied).length || 0);
      }, 0) || 0;

      //Ingredient remove count
      totalCount += modifiersFilter((selectedItem?.modifiers || []), ['ingredient']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => !option.isApplied).length || 0);
      }, 0) || 0;

      //Removals count
      totalCount += modifiersFilter((selectedItem?.modifiers || []), ['removal']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied).length || 0);
      }, 0) || 0;

      //Check base selection
      totalCount += modifiersFilter((selectedItem?.modifiers || []), ['base']).reduce((count: number, modifier: Modifier) => {
        return count + (modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied && !option.isDefaultSelected).length || 0);
      }, 0) || 0;

    }

    return (isNumberReturn ? totalCount : (totalCount > 0 ? ` (${totalCount})` : ''));
  }

  const syncSwiperPosition = (side: string) => {
    const swiperRef = side === 'leftSideDetails' ? leftSwiperRef : rightSwiperRef;
    const details = side === 'leftSideDetails' ? leftSideDetails : rightSideDetails;
    const swiper = swiperRef?.current?.swiper; // Safe access with optional chaining

    if (swiper && details?.sideModifier?.mofireOptions) { // Added check for details.sideModifier
      const otherSelected = getSelectedOptionOfOtherSide(side).toLowerCase();
      const filtered = details.sideModifier.mofireOptions.filter(
        opt => otherSelected !== opt.optionName.toLowerCase()
      );

      // Handle if applied option is filtered out (e.g., duplicate)
      const originalApplied = details.sideModifier.mofireOptions.find(opt => opt.isApplied);
      if (originalApplied && !filtered.some(opt => opt.optionId === originalApplied.optionId)) {
        const updated = { ...details };
        if (updated.sideModifier) { // Safe check
          updated.sideModifier.mofireOptions.forEach(opt => { opt.isApplied = false; });
        }
        if (side === 'leftSideDetails') {
          setLeftSideDetails(updated);
        } else {
          setRightSideDetails(updated);
        }
        return;
      }

      const appliedFilteredIdx = filtered.findIndex(opt => opt.isApplied);
      const targetIndex = appliedFilteredIdx !== -1 ? appliedFilteredIdx + 1 : 0;


      if (swiper.activeIndex !== targetIndex) {
        swiper.slideTo(targetIndex, 0);
      } else {
        swiper.update();
      }
    }
  };

  useEffect(() => {
    if (isHalfAndHalf) {
      syncSwiperPosition('leftSideDetails');
    }
  }, [leftSideDetails, rightSideDetails]);

  useEffect(() => {
    if (isHalfAndHalf) {
      syncSwiperPosition('rightSideDetails');
    }
  }, [rightSideDetails, leftSideDetails]);

  const handleSideModifierChange = (side: string, modifierType: string, option: ModifierOption) => {
    //const updatedDetails: SideDetails = { ...side === "leftSideDetails" ? leftSideDetails : rightSideDetails } as SideDetails;
    const updatedDetails: SideDetails = structuredClone(side === "leftSideDetails" ? leftSideDetails : rightSideDetails) as SideDetails;
    
    // let optionIndex: number | undefined = 0;
    let optionIndex: number = -1;
    if (modifierType) {
      const modifier = updatedDetails[modifierType.toString() as keyof SideDetails] as Modifier | null;

      if (modifier) {
        // If changing same option, prevent unnecessary updates
        const currentApplied = modifier.mofireOptions.find((opt: ModifierOption) => opt.isApplied);
        if (currentApplied && currentApplied.optionId === option.optionId) {
          return;
        }

        //Set false to all other options
        modifier.mofireOptions.forEach((opt: ModifierOption) => {
          opt.isApplied = (opt.optionId === option.optionId);
        });

        // Compute index relative to the visible slides (the same filter used by Swiper)
        const otherSelected = getSelectedOptionOfOtherSide(side)?.toLowerCase() || '';
        const visibleOptions = modifier.mofireOptions.filter((opt: ModifierOption) => !opt.isSoldOut && opt.optionName?.toLowerCase() !== otherSelected);

        optionIndex = visibleOptions.findIndex((opt: ModifierOption) => opt.optionId === option.optionId);
      }
    }

    // Apply the updated details to state and move the swiper to the corresponding visible slide
    const targetIndex = optionIndex >= 0 ? optionIndex + 1 : 0; // +1 to account for the leading placeholder slide

    if (side === "leftSideDetails") {
      setLeftSideDetails(updatedDetails);
      // Optional: Immediate slide for the changed side (useEffect will handle the other)
      // console.log((optionIndex + 1));
      const swiper = leftSwiperRef?.current?.swiper;
      if (swiper) {
        swiper.slideTo(targetIndex, 0);
      }
    } else {
      setRightSideDetails(updatedDetails);

      const swiper = rightSwiperRef?.current?.swiper;
      if (swiper) {
        swiper.slideTo(targetIndex, 0);
      }
    }

    // trigger re-calculation of totals if needed
    // const updatedItem = { ...selectedItem } as ItemObj;
    // setSelectedItem(updatedItem);
  };

  useEffect(()=>{
    const appliedOptionId = leftSideDetails?.sideModifier?.mofireOptions
      .filter((option: ModifierOption) => !option.isSoldOut)
      .find((option: ModifierOption) => option.isApplied)?.optionId;
      
      // Early return if value hasn't changed - prevents infinite loops
      if (appliedOptionId && lastLeftAppliedRef.current === appliedOptionId) return;
      if (!appliedOptionId && lastLeftAppliedRef.current === "-1") return;

      if (handleSlideChange && appliedOptionId) {
        lastLeftAppliedRef.current = appliedOptionId;
        handleSlideChange(appliedOptionId, true, "leftSideDetails");
      }
      else if (handleSlideChange) {
        lastLeftAppliedRef.current = "-1";
        handleSlideChange("-1", true, "leftSideDetails");
      }

  },[leftSideDetails, handleSlideChange]);

  useEffect(()=>{
    const appliedOptionId = rightSideDetails?.sideModifier?.mofireOptions
      .filter((option: ModifierOption) => !option.isSoldOut)
      .find((option: ModifierOption) => option.isApplied)?.optionId;
      
      // Early return if value hasn't changed - prevents infinite loops
      if (appliedOptionId && lastRightAppliedRef.current === appliedOptionId) return;
      if (!appliedOptionId && lastRightAppliedRef.current === "-1") return;

      if (handleSlideChange && appliedOptionId) {
        lastRightAppliedRef.current = appliedOptionId;
        handleSlideChange(appliedOptionId, true, "rightSideDetails");
      }
      else if (handleSlideChange) {
        lastRightAppliedRef.current = "-1";
        handleSlideChange("-1", true, "rightSideDetails");
      }
      
  },[rightSideDetails, handleSlideChange]);

  const handleSideSliderChange = (side: string, slideId: string | null) => {
    if (slideId !== null) {
      // Find the updated details for the specific side
      const updatedDetails: SideDetails = { ...side === "leftSideDetails" ? leftSideDetails : rightSideDetails } as SideDetails;
      // console.log(updatedDetails, side, slideId);

      //
      if (updatedDetails.sideModifier && updatedDetails.sideModifier.mofireOptions && updatedDetails.sideModifier.mofireOptions.length > 0) {
        updatedDetails.sideModifier.mofireOptions.filter((option: ModifierOption) => !option.isSoldOut).forEach((option: ModifierOption) => {
          option.isApplied = (option.optionId === slideId);
        });
        // console.log("slider", updatedDetails.sideModifier.mofireOptions.findIndex((opt: ModifierOption) => opt.isApplied));
        

        if (side === "leftSideDetails") {
          setLeftSideDetails(updatedDetails);
          // console.log(updatedDetails);
        } else {
          setRightSideDetails(updatedDetails);
        }

        //trigger re-calculation of totals
        // const updatedItem = { ...selectedItem } as ItemObj;
        // setSelectedItem(updatedItem);
      }
    }
  };


  const getSelectedOptionOfOtherSide = (side: string) => {
    if (side === "leftSideDetails") {
      return rightSideDetails?.sideModifier?.mofireOptions.find((opt: ModifierOption) => opt.isApplied)?.optionName || '';
      
    } else {
      return leftSideDetails?.sideModifier?.mofireOptions.find((opt: ModifierOption) => opt.isApplied)?.optionName || '';
    }
  };

  const cartId = (cart && cart.orderId && cart.orderId !== "" ? "?c=" + encryptData(cart.orderId) : "");

  const scrollToCustomization = () => {
    const el = document.getElementById('customizeSection');
    if (el) {
      if(isBottomSheet){
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      else{
        el.style.scrollMarginTop = '80px';
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const scrollableContainer = document.querySelector('#itemview-main');
    if (scrollableContainer) {
      scrollableContainer.addEventListener('scroll', () => {
        const scrollTop = scrollableContainer.scrollTop;
        setShowScrollToCustomization(scrollTop > 100);
      });
    }

    return () => {
      if (scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', () => {});
      }
    };
  }, []);

  useEffect(() => {
    const otherSelected = getSelectedOptionOfOtherSide("leftSideDetails")?.toLowerCase() ?? "";

    const filteredLeft = (leftSideDetails?.sideModifier?.mofireOptions || []).filter(
      (optionItem: ModifierOption) =>
        !optionItem.isSoldOut &&
        optionItem.optionName?.toLowerCase() !== otherSelected
    );

    setAvailableLeftOptionsMod(filteredLeft);

  }, [leftSideDetails,rightSideDetails ,showRightPizzaSheet, getSelectedOptionOfOtherSide("leftSideDetails")]);

  useEffect(() => {
    const otherSelected = getSelectedOptionOfOtherSide("rightSideDetails")?.toLowerCase() ?? "";

    const filteredRight = (rightSideDetails?.sideModifier?.mofireOptions || []).filter(
      (optionItem: ModifierOption) =>
        !optionItem.isSoldOut &&
        optionItem.optionName?.toLowerCase() !== otherSelected
    );

    setAvailableRightOptionsMod(filteredRight);

  }, [leftSideDetails,rightSideDetails, showRightPizzaSheet , getSelectedOptionOfOtherSide("rightSideDetails")]);


  const updateVisibleBullets = (swiper: SwiperClass) => {
    const bullets = swiper.pagination?.el?.querySelectorAll('.swiper-pagination-bullet');
    if (!bullets || bullets.length === 0) return;

    const activeIndex = swiper.activeIndex;
    const totalBullets = bullets.length;
    
    bullets.forEach((bullet: Element) => {
      bullet.classList.remove('visible', 'bullet-prev', 'bullet-next', 'sliding-out-left', 'sliding-out-right');
    });

    let startIndex, endIndex;
    
    if (totalBullets <= 3) {
      startIndex = 0;
      endIndex = totalBullets - 1;
    } else if (activeIndex === 0) {
      startIndex = 0;
      endIndex = 2;
    } else if (activeIndex === totalBullets - 1) {
      startIndex = totalBullets - 3;
      endIndex = totalBullets - 1;
    } else {
      startIndex = activeIndex - 1;
      endIndex = activeIndex + 1;
    }

    for (let i = startIndex; i <= endIndex; i++) {
      if (bullets[i]) {
        bullets[i].classList.add('visible');
        
        if (i === activeIndex - 1) {
          bullets[i].classList.add('bullet-prev');
        } else if (i === activeIndex + 1) {
          bullets[i].classList.add('bullet-next');
        }
      }
    }
  };


  const handleSetModifiers = (newModifiers: Modifier[]) => {
    // console.log("Updating modifiers for", newModifiers);
    if (isHalfAndHalf) {
      if (customizeSide === 'leftSideDetails') {
        setLeftSideDetails(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            modifiers: newModifiers
          };
        });
      } else {
        setRightSideDetails(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            modifiers: newModifiers
          };
        });
      }
    } else {
      setSelectedItem(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          modifiers: newModifiers
        };
      });
    }
  };


  useEffect(() => {
    if (leftSwiperRef.current?.swiper) {
      const swiper = leftSwiperRef.current.swiper;
      const bullets = swiper.pagination?.el?.querySelectorAll('.swiper-pagination-bullet');
      
      if (bullets) {
        bullets.forEach((bullet: Element, index: number) => {
          const option = availableLeftOptionsMod[index - 1];
          const img = bullet.querySelector('img');
          if (img) {
            const imgSrc = option?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png';
            img.src = imgSrc;
            img.alt = `Option ${index}`;
          }
        });
        
        updateVisibleBullets(swiper);
      }
    }
  }, [availableLeftOptionsMod]);

  useEffect(() => {
    if (rightSwiperRef.current?.swiper) {
      const swiper = rightSwiperRef.current.swiper;
      const bullets = swiper.pagination?.el?.querySelectorAll('.swiper-pagination-bullet');
      
      if (bullets) {
        bullets.forEach((bullet: Element, index: number) => {
          const option = availableRightOptionsMod[index - 1];
          const img = bullet.querySelector('img');
          if (img) {
            const imgSrc = option?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png';
            img.src = imgSrc;
            img.alt = `Option ${index}`;
          }
        });

        updateVisibleBullets(swiper);
      }
    }
  }, [availableRightOptionsMod]);


  if (!hasHydrated) {
    return <PreloaderComponent />; // Prevent rendering until store is hydrated
  }

  // console.log("getSelectedOptionOfOtherSide",getSelectedOptionOfOtherSide("leftSideDetails"));
  // console.log(getSelectedOptionOfOtherSide("rightSideDetails"));
  // console.log("leftSideDetails",leftSideDetails?.sideModifier?.mofireOptions);

  // console.log("selectedItem",selectedItem);
  // console.log("leftSideDetails",leftSideDetails);

  /* if(!loadedItemDetails){
    return (<div className={styles.itemComponentContainer}>
      Cannot load item details. Please try again later.
    </div>);
  } */

  return (
    <RemoveScroll enabled={showCustomize || showLeftPizzaSheet || showRightPizzaSheet || showSuccessSheet}>
      {!isBottomSheet && <Navbar pageTitle={(selectedItem?.itemName || 'Item Details')} />}

      <main id='itemview-main' className={styles.main + (inputOnFocus ? ` ${styles.inputOnFocus}` : '') + (isBottomSheet ? ` ${styles.withOutNavbar}` : '') + (selectedItem?.isSoldOut ? ` ${styles.soldOut}` : '') + (isComboHalfAndHalf ? ` ${styles.comboHalfItem}` : '')}>
        <Container id='itemview-container' className={styles.itemContainer}>
          <div style={{ display: isBottomSheet ? 'none' : 'block'}}>
            <Button variant='secondary' id={`item-view-go-back-button`} onClick={() => window.history.back()} className={styles.singleViewBackButton}><X size={20} /></Button>
          </div>

          {!isItemDetailsLoaded?
            <PreloaderComponent />
          :!loadedItemDetails ? (
            <div className={styles.errorContainer}>
              <Lottie animationData={emptyItemsLottie} className={styles.emptyLottie} loop={true} />
              <h3>{t('ItemView.NOT_FOUND')}</h3>
              <p>{t('ItemView.NOT_FOUND_DESCRIPTION')}</p>
              <Button variant='secondary' id={`item-view-go-back-button`} onClick={() => window.history.back()} className={styles.backButton}>{t('ItemView.GO_BACK')}</Button>
            </div>
          ) : (
            <>

            <div className={styles.itemImageSection} style={{flexDirection: !isHalfAndHalf ? 'column-reverse' : 'column',marginTop: selectedItem && selectedItem?.itemName?.length > 40 ? '26px' : '0'}}>
              {!isHalfAndHalf && <div className={styles.itemOverview}>
              <div className={styles.rating} style={{ display: selectedItem?.rating && selectedItem?.rating.itemRating && selectedItem?.rating.itemRating  > 0 ? 'block' : 'none' }}>
                {selectedItem?.rating && selectedItem?.rating.itemRating > 0 ? <>
                <div className={styles.rating}>
                  <span className={styles.star}><Star size={20} /></span>
                  <span className={styles.ratingDetails}>
                    <span className={styles.ratingText}>
                      {Number(selectedItem.rating.itemRating) % 1 === 0
                        ? Number(selectedItem.rating.itemRating).toFixed(1)
                        : selectedItem.rating.itemRating}
                    </span>
                    <span className={styles.reviewCount}>{selectedItem.rating.totalReviewCount} {t('ItemView.REVIEW_Text')}</span>
                  </span>
                </div>
                </> : null}

                {/* <div className={styles.starRating}>
                  {Object.keys(starEnumObj).map((star: string, index: number) => {
                    const selectedStarIndex = Object.keys(starEnumObj).findIndex(key => starEnumObj[Number(key)] === selectedItem?.rating);
  
                    return (<AiFillStar
                      id={`star-rating-${index}`}
                      key={star}
                      className={selectedStarIndex !== -1 ? ((selectedStarIndex + 1) < parseInt(star) ? styles.inactive : '') : styles.inactive}
                    />)
                  })}
                </div> */}

              </div>
              <div className={styles.vegetarianStatus}>
                <div className={styles.outOfStock  + (selectedItem?.isVegetarian === null ? `${styles.hidden}`
                  : `${selectedItem?.isVegetarian ? ` ${styles.veg}` : ''}`)}>
                  <span className={styles.dot}></span>
                </div>
                <p>{selectedItem?.isVegetarian !== null && (selectedItem?.isVegetarian ? t('ItemView.VEGETARIAN') : t('ItemView.NON_VEGETARIAN'))}</p>
              </div>

              {(!selectedItem?.isSoldOut && !isHalfAndHalf && modifiersFilter((selectedItem?.modifiers || []), ['notdietary']).length > 0) && <div className={styles.customizeSection+ ' ' + styles.top}>
                <Button variant='warning' id={`item-view-customize-button`} className={styles.customizeButton + ' ' + styles.top} onClick={() => {
                  setShowCustomize(true);
                  event({
                    action: 'click_customize_button',
                    category: 'engagement',
                    label: `Clicked Customize button on Item View for itemId: ${selectedItem?.itemId || ''}`
                  })
                }}>
                  <span>{t('ItemView.CUSTOMIZE')}{!isHalfAndHalf?getCustomizeCount():<></>}</span>
                  <Plus size={15} />
                </Button>
              </div>}

              </div>}

              {/* Item view back button */}
              {/* <div style={{ display: isBottomSheet ? 'none' : 'block'}}>
                <Button variant='secondary' id={`item-view-go-back-button`} onClick={() => window.history.back()} className={styles.singleViewBackButton}><X size={20} /></Button>
              </div> */}

              {/* <div className={styles.heartIcon}>
              <Heart size={24} />
              </div> */}

              {/* half and half */}
              {isHalfAndHalf?
              <>
              {!selectedItem?.isSoldOut && <div className={styles.pizzaSelection}>
                <button id={`left-side-selector-button`} className={styles.dropdownSelector} onClick={() => {
                  toggleHalfHalfSelect(true);
                  event({
                    action: 'click_left_side_selector',
                    category: 'engagement',
                    label: `Clicked Left Side Selector on Half & Half Item View for itemId: ${selectedItem?.itemId || ''}`
                  })
                }}>
                  <span>{leftSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.optionDisplayName || (t('ItemView.SELECT_LEFT_SIDE'))}</span>
                  <ChevronDown/>
                </button>

                <div className={styles.divider + styles.dividerSecond} />

                <button id={`right-side-selector-button`} className={styles.dropdownSelector} onClick={() => {
                  toggleHalfHalfSelect(false);
                  event({
                    action: 'click_right_side_selector',
                    category: 'engagement',
                    label: `Clicked Right Side Selector on Half & Half Item View for itemId: ${selectedItem?.itemId || ''}`
                  })
                }}>
                  <span>{rightSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.optionDisplayName || (t('ItemView.SELECT_RIGHT_SIDE'))}</span>
                  <ChevronDown/>
                </button>
              </div>}

              {/* <div className={styles.vegetarianStatusRow}>
                <div className={styles.vegetarianStatus}>
                  <div className={styles.outOfStock  + (leftSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian === null ? `${styles.hidden}`
                    : `${!leftSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian ? ` ${styles.veg}` : ''}`)}>
                    <span className={styles.dot}></span>
                  </div>
                  <p>{!leftSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian === null &&
                  (leftSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian ? t('ItemView.VEGETARIAN') : t('ItemView.NON_VEGETARIAN'))}</p>
                </div>
                <div className={styles.vegetarianStatus}>
                  <div className={styles.outOfStock  + (rightSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian === null ? `${styles.hidden}`
                    : `${!rightSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian ? ` ${styles.veg}` : ''}`)}>
                    <span className={styles.dot}></span>
                  </div>
                  <p>{!rightSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian === null &&
                  (rightSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.isVegetarian ? t('ItemView.VEGETARIAN') : t('ItemView.NON_VEGETARIAN'))}</p>
                </div>
              </div> */}

              <div className={styles.selectionArea}>
                <div className={styles.pizzaVisual}>
                  <div className={styles.pizzaPreview}>
                    <Swiper
                      ref={leftSwiperRef as Ref<SwiperRef>}
                      modules={[Pagination]}
                      pagination={{ 
                        clickable: true, 
                        enabled: (!selectedItem?.isSoldOut),
                        dynamicBullets: false,
                        dynamicMainBullets: 1,
                        renderBullet: (index, className) => {
                          const option = (availableLeftOptionsMod.length > 0 ? availableLeftOptionsMod[index - 1] : null);
                          const imgSrc = option?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png';
                          return `<span class="${className}"><img src="${imgSrc}" alt="Option ${index}" /></span>`;
                        }
                      }}
                    direction="vertical"
                    slidesPerView={1}
                    // initialSlide={leftSideDetails?.sideModifier?.mofireOptions.findIndex((optionItem: ModifierOption) => optionItem.isApplied) || 0}
                    initialSlide={0}
                    spaceBetween={0}
                    mousewheel={!selectedItem?.isSoldOut}
                    draggable={!selectedItem?.isSoldOut}
                    allowSlideNext={!selectedItem?.isSoldOut}
                    allowSlidePrev={!selectedItem?.isSoldOut}
                    allowTouchMove={!selectedItem?.isSoldOut}
                    onSlideChange={(slide) => {
                      const activeSlideId = slide.slides[slide.activeIndex].getAttribute('id');
                      handleSideSliderChange("leftSideDetails", activeSlideId);
                      updateVisibleBullets(slide);

                      event({
                        action: 'slide_change_left_side',
                        category: 'engagement',
                        label: `Slide changed on Left Side Slider for itemId: ${selectedItem?.itemId || ''}`
                      });

                    }}
                    className={styles.carouselContainer+ ' ' + styles.leftCarousel}
                    >
                    <SwiperSlide id='-1'>
                      <div className={styles.pizzaOption}>
                      <Image src="/assets/images/placeholderActualSize.png" alt="Left Half Item" className={styles.leftHalf} width={350} height={350} />
                      </div>
                    </SwiperSlide>

                    {(leftSideDetails?.sideModifier?.mofireOptions || []).filter((optionItem: ModifierOption) => !optionItem.isSoldOut).map((optionItem: ModifierOption, index: number) => (
                      <React.Fragment key={index}>
                      {getSelectedOptionOfOtherSide("leftSideDetails").toLowerCase() !== optionItem.optionName.toLowerCase() && (
                        <SwiperSlide key={index} id={optionItem.optionId}>
                        <div className={`${styles.pizzaOption} ${optionItem.isApplied ? styles.selected : ''}`}>
                          <PhotoProvider>
                          <PhotoView src={optionItem?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png'}>
                            <Image 
                            id={`left-side-option-${index}-image`}
                            src={optionItem?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png'} 
                            alt={optionItem?.optionDisplayName || 'Left Half Item'} 
                            className={styles.leftHalf} 
                            width={350}
                            height={350}
                            />
                          </PhotoView>
                        </PhotoProvider>
                        </div>
                      </SwiperSlide>)}
                      </React.Fragment>
                    )) || <></>}

                    </Swiper>
                    
                    <div className={styles.divider} />

                    <Swiper
                    ref={rightSwiperRef as Ref<SwiperRef>}
                    modules={[Pagination]}
                    pagination={{ 
                      clickable: true, 
                      enabled: (!selectedItem?.isSoldOut),
                      dynamicBullets: false,
                      dynamicMainBullets: 1,
                      renderBullet: (index, className) => {
                        const option = (availableRightOptionsMod.length > 0 ? availableRightOptionsMod[index - 1] : null);
                        const imgSrc = option?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png';
                        return `<span class="${className}"><img src="${imgSrc}" alt="Option ${index}" /></span>`;
                      }
                    }}
                    direction="vertical"
                    slidesPerView={1}
                    // initialSlide={rightSideDetails?.sideModifier?.mofireOptions.findIndex((optionItem: ModifierOption) => optionItem.isApplied) || 0}
                    initialSlide={0}
                    spaceBetween={0}
                    mousewheel={!selectedItem?.isSoldOut}
                    draggable={!selectedItem?.isSoldOut}
                    allowSlideNext={!selectedItem?.isSoldOut}
                    allowSlidePrev={!selectedItem?.isSoldOut}
                    allowTouchMove={!selectedItem?.isSoldOut}
                    onSlideChange={(slide) => {
                      const activeSlideId = slide.slides[slide.activeIndex].getAttribute('id');
                      handleSideSliderChange("rightSideDetails", activeSlideId);
                      updateVisibleBullets(slide);

                      event({
                        action: 'slide_change_right_side',
                        category: 'engagement',
                        label: `Slide changed on Right Side Slider for itemId: ${selectedItem?.itemId || ''}`
                      });

                    }}
                    className={styles.carouselContainer + ' ' + styles.rightCarousel}
                    >

                    <SwiperSlide id='-2'><div className={styles.pizzaOption}>
                      <Image src="/assets/images/placeholderActualSize.png" alt="Right Half Item" 
                      className={styles.rightHalf} 
                      width={350}
                      height={350}
                      />
                      </div>
                    </SwiperSlide>

                    {rightSideDetails?.sideModifier?.mofireOptions.filter((optionItem: ModifierOption) => !optionItem.isSoldOut).map((optionItem: ModifierOption, index: number) => (
                      <React.Fragment key={index}>
                      {getSelectedOptionOfOtherSide("rightSideDetails").toLowerCase() !== optionItem.optionName.toLowerCase() && (
                        <SwiperSlide key={index} id={optionItem.optionId}>
                        <div className={`${styles.pizzaOption} ${optionItem.isApplied ? styles.selected : ''}`}>
                          <PhotoProvider>
                          <PhotoView src={optionItem?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png'}>
                            <Image 
                              id={`right-side-option-${index}-image`}
                              src={optionItem?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png'} 
                              alt={optionItem?.optionDisplayName || 'Right Half Item'} 
                              className={styles.rightHalf} 
                              width={350}
                              height={350}
                              />
                          </PhotoView>
                          </PhotoProvider>
                        </div>
                      </SwiperSlide>
                      )}
                      </React.Fragment>
                    )) || <></>} 

                    </Swiper>
                  </div>
                </div>
              </div>

              <button 
                className={`${styles.scrollToCustomizationButton} ${showScrollToCustomization ? styles.hidden : ''}`}
                onClick={scrollToCustomization}
                aria-label="Scroll to customization"
              >
                <ChevronsDown size={20} color='#0e711bff'/>
              </button>

              {!selectedItem?.isSoldOut && <div id="customizeSection" className={styles.customizeSection + (isHalfAndHalf ? ` ${styles.halfAndHalf}` : '')}>
                {modifiersFilter((leftSideDetails?.modifiers || []), ['notdietary']).length > 0 && <Button variant='warning' id={`left-side-customize-button`} className={styles.customizeButton} onClick={() => {
                  handleShowCustomize(true, "leftSideDetails");
                
                  event({
                    action: 'click_left_side_customize_button',
                    category: 'engagement',
                    label: `Clicked Left Side Customize button on Half & Half Item View for itemId: ${selectedItem?.itemId || ''}`
                  })
                }}>
                  <span>{t('ItemView.CUSTOMIZE')}{getCustomizeCount(true, "left")}</span>
                  <Plus size={15} />
                </Button>}
                
                {/* <Button variant='light' className={styles.halfHalfButton} onClick={() => toggleHalfHalf()}>
                  <Image src="/assets/icons/single-pizza-icon.svg" alt="Half half toggle" width={12} height={12} />
                </Button> */}

                {modifiersFilter((rightSideDetails?.modifiers || []), ['notdietary']).length > 0 && <Button variant='warning' id={`right-side-customize-button`} className={styles.customizeButton} onClick={() => {
                  handleShowCustomize(true, "rightSideDetails");
                
                  event({
                    action: 'click_right_side_customize_button',
                    category: 'engagement',
                    label: `Clicked Right Side Customize button on Half & Half Item View for itemId: ${selectedItem?.itemId || ''}`
                  })
                }}>
                  <span>{t('ItemView.CUSTOMIZE')}{getCustomizeCount(true, "right")}</span>
                  <Plus size={15} />
                </Button>}
              </div>}
              </>
              :<>
              {/* Image Section */}
              <div className={styles.imageSection}>
                <div className={styles.imageWrapper}>
                <Carousel
                  interval={null}
                  className={styles.imageCarousel}
                  indicators={selectedItem?.imageUrls && selectedItem?.imageUrls.length > 1 ? true : false}
                  controls={selectedItem?.imageUrls && selectedItem?.imageUrls.length > 1 ? true : false}
                >
                  {selectedItem?.imageUrls && selectedItem?.imageUrls.length > 0 && selectedItem?.imageUrls.map((image: ImageUrlObj, index: number) => (
                  <Carousel.Item key={index}>
                    <PhotoProvider>
                    <PhotoView src={image.imageUrl}>
                      <div className={styles.imageContainer}>
                      <Image
                        id={`item-view-image-${index}`}
                        src={image.imageUrl}
                        alt={image.imageName || 'Item Image'}
                        fill
                        sizes="(max-width: 600px) 100vw, 50vw"
                        className={styles.itemImage}
                        placeholder='blur'
                        blurDataURL="/assets/images/placeholder.png"
                      />
                      </div>
                    </PhotoView>
                    </PhotoProvider>
                  </Carousel.Item>
                  ))}
                </Carousel>

                </div>
                
              </div>
              {(!selectedItem?.isSoldOut && !isHalfAndHalf && modifiersFilter((selectedItem?.modifiers || []), ['notdietary']).length > 0) && <div className={styles.customizeSection}>
                {/* <Button variant='light' className={styles.halfHalfButton} onClick={() => toggleHalfAndHalf()}>
                <Image src="/assets/icons/halfhalf-icon.svg" alt="Half half toggle" width={12} height={12} />
                </Button> */}

                <Button variant='warning' id={`item-view-customize-button`} className={styles.customizeButton + ' ' + styles.bottom} onClick={() => {
                  setShowCustomize(true)
                  event({
                    action: 'click_customize_button',
                    category: 'engagement',
                    label: `Clicked Customize button on Item View for itemId: ${selectedItem?.itemId || ''}`
                  })
               }}>
                <span>{t('ItemView.CUSTOMIZE')}{getCustomizeCount()}</span>
                <Plus size={15} />
                </Button>
              </div>}
              </>}
            </div>

            {selectedItem?.isSoldOut && <div className={styles.soldOutBanner}>
              <p>{t('ItemView.SOLD_OUT_MESSAGE')}</p>
            </div>}
            
            {/* Description Section */}
            <div className={styles.contentSection}>
              <div className={styles.upperContent}>

                {selectedItem && selectedItem?.itemName?.length > 55 ? (
                  <CustomTooltip
                    overlayContent={selectedItem?.itemName || ''}
                    placement="top"
                    scrollElement={null}
                  >
                    {selectedItem?.itemName && (
                      <h1 className={styles.itemTitle}>
                        {selectedItem?.itemName.substring(0, 55) + "..."}
                        <span
                          className={styles.outOfStockLabel}
                          style={{ display: selectedItem?.isSoldOut ? "inline" : "none" }}
                        >
                          {t('ItemView.SOLD_OUT')}
                        </span>
                      </h1>
                    )}
                  </CustomTooltip>
                ) : (
                  selectedItem?.itemName && (
                    <h1 className={styles.itemTitle}>{selectedItem?.itemName}
                      <span
                        className={styles.outOfStockLabel}
                        style={{ display: selectedItem?.isSoldOut ? "inline" : "none" }}
                      >
                        {t('ItemView.SOLD_OUT')}
                      </span>
                    </h1>
                  )
                )}

                {/* {selectedItem?.itemName?<p className={styles.itemTitle}>
                  {selectedItem?.itemName} <span className={styles.outOfStockLabel} style={{ display: selectedItem?.isSoldOut ? "inline" : "none" }}> soldOut </span>
                </p> : <></>} */}

                {!isComboHalfAndHalf && selectedItem?.itemDescription?<RichTextViewer html={selectedItem?.itemDescription} />: <></>}

                {/* Show selected add-ons */}
                {!isHalfAndHalf && selectedItem?.modifiers && selectedItem?.modifiers.length > 0?<> 
                  <div className={styles.selectedAddons} style={{ marginBottom: '5px' }}>
                    <div className={styles.addOnsList}>
                      {modifiersFilter((selectedItem?.modifiers || []), ['base']).map((modifier: Modifier) => {
                        const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                        return (
                          <React.Fragment key={modifier.modifierId}>
                            {applied.length > 0 ? (
                              <>{applied.map((opt: ModifierOption, index: number) => {
                                return <span key={index} className={`${styles.addonItem} ${styles.base}`}><BiCircle size={16} /> {opt.optionName} Base</span>;
                              })}</>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.selectedAddons}>
                    <div className={styles.addOnsList}>
                      {modifiersFilter((selectedItem?.modifiers || []), ['add']).map((modifier: Modifier) => {
                        const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                        return (
                          <React.Fragment key={modifier.modifierId}>
                            {applied.length > 0 ? (
                              <>{applied.map((opt: ModifierOption, index: number) => {
                                return <React.Fragment key={index}>
                                  <CustomTooltip
                                    overlayContent={opt.optionName || ''}
                                    placement="top"
                                    scrollElement={null}
                                  >
                                    <span key={index} className={styles.addonItem}>+ {opt.quantity > 1 ? `${opt.quantity} x ` : ''} 
                                      {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}
                                    </span>
                                  </CustomTooltip>
                                </React.Fragment>;
                              })}</>
                            ) : null}
                          </React.Fragment>
                        );
                      })}

                      {modifiersFilter((selectedItem?.modifiers || []), ['ingredient', 'removal']).map((modifier: Modifier) => {
                        const isIngredientModifier = modifier.modifierName.toLowerCase().includes("ingred");
                        const removed = modifier.mofireOptions.filter((option: ModifierOption) => (isIngredientModifier ? !option.isApplied : option.isApplied));
                        return (
                          <React.Fragment key={modifier.modifierId}>
                            {removed.length > 0 ? (
                              <>{removed.map((opt: ModifierOption, index: number) => {
                                return <React.Fragment key={index}>
                                  <CustomTooltip
                                    overlayContent={opt.optionName || ''}
                                    placement="top"
                                    scrollElement={null}
                                  >
                                    <span className={`${styles.addonItem} ${styles.removable}`}>- {opt.quantity > 1 ? `${opt.quantity} x` : ''} 
                                      {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}
                                    </span>
                                  </CustomTooltip>
                                </React.Fragment>;
                              })}</>
                            ) : null}
                          </React.Fragment>
                        );
                      })}

                    </div>
                  </div>

                </>: <></>}

                {/* Show half and half selected add-ons side by side */}
                {isHalfAndHalf && getCustomizeCount(true, "left", true) as number > 0 ? 
                <div className={styles.selectedAddons}>
                  <h5>{t('ItemView.LEFT_SIDE_CUSTOMIZATION')}:</h5>

                  <div className={styles.addOnsList}>
                    {modifiersFilter((leftSideDetails?.modifiers || []), ['base']).map((modifier: Modifier) => {
                      const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                      return (
                        <React.Fragment key={modifier.modifierId}>
                          {applied.length > 0 ? (
                            <>{applied.map((opt: ModifierOption, index: number) => {
                              return <span key={index} className={`${styles.addonItem} ${styles.base}`}><BiCircle size={16} /> {opt.optionName} Base</span>;
                            })}</>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  <div className={styles.addOnsList}>
                    {modifiersFilter((leftSideDetails?.modifiers || []), ['add']).map((modifier: Modifier) => {
                      const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                      return (
                        <React.Fragment key={modifier.modifierId}>
                          {applied.length > 0 ? (
                            <>{applied.map((opt: ModifierOption, index: number) => {
                              return <React.Fragment key={index}>
                                <CustomTooltip
                                  overlayContent={opt.optionName || ''}
                                  placement="top"
                                  scrollElement={null}
                                >
                                  <span className={styles.addonItem}>+ {opt.quantity > 1 ? `${opt.quantity} x ` : ''} 
                                    {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}
                                  </span>
                                </CustomTooltip>
                              </React.Fragment>;
                            })}</>
                          ) : null}
                        </React.Fragment>
                      );
                    })}

                    {(modifiersFilter((leftSideDetails?.modifiers || []), ['ingredient', 'removal'])).map((modifier: Modifier) => {
                      const isIngredientModifier = modifier.modifierName.toLowerCase().includes("ingred");
                      const removed = modifier.mofireOptions.filter((option: ModifierOption) => (isIngredientModifier ? !option.isApplied : option.isApplied));
                      return (
                        <React.Fragment key={modifier.modifierId}>
                          {removed.length > 0 ? (
                            <>{removed.map((opt: ModifierOption, index: number) => {
                              return <React.Fragment key={index}>
                                <CustomTooltip
                                  overlayContent={opt.optionName || ''}
                                  placement="top"
                                  scrollElement={null}
                                >
                                  <span className={`${styles.addonItem} ${styles.removable}`}>- {opt.quantity > 1 ? `${opt.quantity} x ` : ''} 
                                    {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}
                                  </span>
                                </CustomTooltip>
                              </React.Fragment>;
                            })}</>
                          ) : null}
                        </React.Fragment>
                      );
                    })}

                  </div>
                </div>: <></>}

                {isHalfAndHalf && getCustomizeCount(true, "right", true) as number > 0 ? 
                <div className={styles.selectedAddons}>
                  <h5>{t('ItemView.RIGHT_SIDE_CUSTOMIZATION')}:</h5>

                  <div className={styles.addOnsList}>
                    {modifiersFilter((rightSideDetails?.modifiers || []), ['base']).map((modifier: Modifier) => {
                      const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                      return (
                        <React.Fragment key={modifier.modifierId}>
                          {applied.length > 0 ? (
                            <>{applied.map((opt: ModifierOption, index: number) => {
                              return <span key={index} className={`${styles.addonItem} ${styles.base}`}><BiCircle size={16} /> {opt.optionName} Base</span>;
                            })}</>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  <div className={styles.addOnsList}>
                    {modifiersFilter((rightSideDetails?.modifiers || []), ['add']).map((modifier: Modifier) => {
                      const applied = modifier.mofireOptions.filter((option: ModifierOption) => option.isApplied);
                      return (
                        <React.Fragment key={modifier.modifierId}>
                          {applied.length > 0 ? (
                            <>{applied.map((opt: ModifierOption, index: number) => {
                              return <React.Fragment key={index}>
                                <CustomTooltip
                                  overlayContent={opt.optionName || ''}
                                  placement="top"
                                  scrollElement={null}
                                >
                                  <span className={styles.addonItem}>+ {opt.quantity > 1 ? `${opt.quantity} x ` : ''} 
                                    {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}
                                  </span>
                                </CustomTooltip>
                              </React.Fragment>;
                            })}</>
                          ) : null}
                        </React.Fragment>
                      );
                    })}

                    {modifiersFilter((rightSideDetails?.modifiers || []), ['ingredient', 'removal']).map((modifier: Modifier) => {
                      const isIngredientModifier = modifier.modifierName.toLowerCase().includes("ingred");
                      const removed = modifier.mofireOptions.filter((option: ModifierOption) => (isIngredientModifier ? !option.isApplied : option.isApplied));
                      
                      return (
                        <React.Fragment key={modifier.modifierId}>
                          {removed.length > 0 ? (
                            <>{removed.map((opt: ModifierOption, index: number) => {
                              return <React.Fragment key={index}>
                                <CustomTooltip
                                  overlayContent={opt.optionName || ''}
                                  placement="top"
                                  scrollElement={null}
                                >
                                  <span className={`${styles.addonItem} ${styles.removable}`}>- {opt.quantity > 1 ? `${opt.quantity} x ` : ''} 
                                    {opt.optionName.substring(0, 20) + (opt.optionName.length > 20 ? '...' : '')}
                                  </span>
                                </CustomTooltip>
                              </React.Fragment>;
                            })}</>
                          ) : null}
                        </React.Fragment>
                      );
                    })}

                  </div>
                </div>: <></>}
                
                {isComboHalfAndHalf && invalidModifiers.length > 0 && (
                  <div className={styles.paymentLimitWarning}>
                    <Image
                      src={"/assets/images/paylimit-warning.png"}
                      alt="Warning" width={23} height={23}
                      />                        
                    <span className={styles.itemNameWarningSingleItem}>
                      Please add{" "}
                      {invalidModifiers
                        .map(m => {
                          const updatedName = m.modifierName
                            .replace(/Pizza 1\s*/gi, "Left Side ")
                            .replace(/Pizza 2\s*/gi, "Right Side ");
                          return `${m.minCount > 1 ? (m.minCount) : ""} ${updatedName} `;
                        })
                        .join(", ")}
                    </span>
                  </div>
                ) }

                {/* Size and Dietary Options */}
                {<div className={styles.optionsSection}>
                  {selectedItem && !isComboHalfAndHalf && selectedItem.variations?.length > 1 && <div className={styles.optionGroup}>
                    <h2>{t('ItemView.SIZE')}</h2>
                    <div className={styles.options}>

                      {/* <Swiper
                        spaceBetween={10}
                        slidesPerView={2}
                        breakpoints={{
                          0: { slidesPerView: 2 },
                          464: { slidesPerView: 3 },
                          768: { slidesPerView: 4 },
                          1024: { slidesPerView: 5 },
                          1440: { slidesPerView: 6 }
                        }}
                        navigation={false}
                        pagination={{ clickable: true }}
                      > */}
                        {selectedItem?.variations && selectedItem?.variations.map((variation: Variation, index: number) => (
                          <React.Fragment key={variation.variationId}>
                          {/* <SwiperSlide key={variation.variationId} style={{ width: '150px' }}> */}
                            <button 
                              id={"item-size-option-" + index}
                              className={`${styles.optionButton} ${(!selectedItem?.isSoldOut && variation.isApplied) ? styles.selected : ''}`}
                              onClick={() => {
                                handleSizeChange(index);
                                event({
                                  action: 'select_size_option',
                                  category: 'engagement',
                                  label: `Selected size option: ${variation.variationName} for itemId: ${selectedItem?.itemId || ''}`
                                });
                              }}
                              data-variant={removeSpacesFromString((variation?.variationName || ''))}
                              disabled={variation.isSoldOut || selectedItem?.isSoldOut}
                            >
                                {variation.variationName} {' '}
                                <span style={{ textDecoration: variation.isSoldOut ? 'line-through' : 'none' }}>
                                ( {formatCurrency((variation?.variationPrice?.amount || 0), (variation?.variationPrice?.currency || 'USD'), 'en-US')} )
                                </span>
                                <span className={styles.buttonOutOfStock} style={{ display: variation.isSoldOut ? 'inline' : 'none' }}>
                                  {t('ItemView.SOLD_OUT')}
                                </span>
                            </button>
                          {/* </SwiperSlide> */}
                        </React.Fragment>
                        ))}
                      {/* </Swiper> */}
                    </div>
                  </div>}

                  {!selectedItem?.isSoldOut && selectedItem?.modifiers && selectedItem?.modifiers.length > 0 && modifiersFilter((selectedItem?.modifiers || []), ['dietary']).map((modifier: Modifier) => (
                    <div key={modifier.modifierId} className={styles.optionGroup}>
                      <h2>{t('ItemView.DIETARY_OPTIONS')}</h2>
                      <div className={styles.options}>
                        
                        {/* <Swiper
                          spaceBetween={10}
                          slidesPerView={4}
                          navigation={false}
                          pagination={{ clickable: true }}
                          draggable={true}
                        > */}
                          {modifier?.mofireOptions && modifier?.mofireOptions.map((option: ModifierOption, index: number) => (
                            <React.Fragment key={option.optionId}>
                            {/* <SwiperSlide key={option.optionId}> */}
                              <button 
                                id={"item-dietary-option-" + index}
                                className={`${styles.optionButton} ${(option.isApplied) ? styles.selected : ''}`}
                                onClick={() => {
                                  if(isComboHalfAndHalf){
                                    onModifierChange?.(modifier.modifierId, index, false, !option.isApplied, true);
                                  } else{
                                    handleModifierChange(modifier.modifierId, index, false, !option.isApplied);
                                  }
                                  // onModifierChange?.(modifier.modifierId, index, false, !option.isApplied,true);
                                  // handleModifierChange(modifier.modifierId, index, false, !option.isApplied); 
                                  event({
                                    action: 'select_modifier_option',
                                    category: 'engagement',
                                    label: `Selected modifier option: ${option.optionName} for itemId: ${selectedItem?.itemId || ''}`
                                  });
                                }}
                                data-modifier={removeSpacesFromString((option?.optionName || ''))}
                                disabled={option.isSoldOut}
                              >
                                {option.optionName } {option?.optionPrice?.amount? `(+${formatCurrency(option.optionPrice.amount,option.optionPrice.currency || 'USD','en-US')})`
                                  : ''}
                              </button>
                            {/* </SwiperSlide> */}
                            </React.Fragment>
                          ))}

                          {/* <SwiperSlide>
                            <button 
                              className={`${styles.optionButton} ${dietaryOption === 'Normal' ? styles.selected : ''}`}
                              onClick={() => handleDietaryChange('Normal')}
                            >
                              Normal
                            </button>
                          </SwiperSlide>
                          <SwiperSlide>
                            <button 
                              className={`${styles.optionButton} ${dietaryOption === 'Gluten Free' ? styles.selected : ''}`}
                              onClick={() => handleDietaryChange('Gluten Free')}
                            >
                              Gluten Free (+$3.00)
                            </button>
                          </SwiperSlide> */}
                        {/* </Swiper> */}

                      </div>
                    </div>
                  ))}

                  {!isComboHalfAndHalf && !selectedItem?.isSoldOut && <div className={styles.optionGroup}>
                    <h2>{t('ItemView.ADDITIONAL_NOTES')}</h2>
                    <textarea 
                      id='item-view-additional-notes'
                      className={styles.notesInput} 
                      placeholder={t('ItemView.ADD_SPECIAL_INSTRUCTIONS')}
                      value={selectedItem?.notes || ''}
                      onChange={(e) => {
                        if(selectedItem){
                          if(e.target.value.length > notesCharLimit) {
                            return;
                          }

                          const updatedItemObj = { ...selectedItem };
                          updatedItemObj.notes = e.target.value;
                          setSelectedItem(updatedItemObj);
                        }
                      }}
                      onFocus={() => setInputOnFocus(true)}
                      onBlur={() => setInputOnFocus(false)}
                    />

                    <div className={styles.charCount}>{selectedItem?.notes ? selectedItem?.notes.length : 0}/{notesCharLimit}</div>
                  </div>}

                  {/* <div className={styles.optionGroup}>
                    <h2>Dietary Options</h2>
                    <div className={styles.options}>
                      <button 
                        className={`${styles.optionButton} ${dietaryOption === 'Normal' ? styles.selected : ''}`}
                        onClick={() => handleDietaryChange('Normal')}
                      >
                        Normal
                      </button>
                      <button 
                        className={`${styles.optionButton} ${dietaryOption === 'Gluten Free' ? styles.selected : ''}`}
                        onClick={() => handleDietaryChange('Gluten Free')}
                      >
                        Gluten Free (+$3.00)
                      </button>
                    </div>
                  </div> */}

                </div>}
              </div>

              {/* Order Section */}
              {!selectedItem?.isSoldOut && <div className={styles.orderSection + (isBottomSheet ? ` ${styles.withOutNavbar}` : '')}>
                {!isComboHalfAndHalf && <div className={styles.quantitySelector}>
                  <button id={`item-view-quantity-decrease-button`} onClick={() => {
                    handleQuantityChange(-1)
                    
                    event({
                      action: 'decrease_item_quantity',
                      category: 'engagement',
                      label: `Decreased item quantity on Item View for itemId: ${selectedItem?.itemId || ''}`
                    })
                  }}>
                    <Minus size={16} />
                  </button>

                  <span>{(selectedItem?.quantity || 1)}</span>

                  <button id={`item-view-quantity-increase-button`} onClick={() => {
                    if((selectedItem?.allowOrderItemQuantities ?? false) && (selectedItem?.orderItemMaxQuantity || 0) > 0 && (Number(selectedItem?.orderItemMaxQuantity) || 0) <= (Number(selectedItem?.quantity) || 1)){
                      alertService.warn(t('ItemView.MAX_LIMIT_REACHED', { maxLimit: (selectedItem?.orderItemMaxQuantity || 1) }));
                      return;
                    }

                    handleQuantityChange(1);

                    event({
                      action: 'increase_item_quantity',
                      category: 'engagement',
                      label: `Increased item quantity on Item View for itemId: ${selectedItem?.itemId || ''}`
                    })
                  }}>
                    <Plus size={16} />
                  </button>
                </div>}

                {!isComboHalfAndHalf ? <button
                  id={`item-view-order-button`}
                  onClick={() => {
                    saveOrder();

                    event({
                      action: 'save_order',
                      category: 'engagement',
                      label: `Saved order on Item View for itemId: ${selectedItem?.itemId || ''}`
                    })
                  }}
                  className={styles.orderButton}
                >
                  {selectedItem && selectedItem.itemUId?t("common.update"):t("ItemView.ADD_TO_CART")} ({totalPrice})
                   </button>:<div className={styles.buttonContainer}>
                    {<button className={styles.cancelButton} onClick={() => {
                    // CloseHalfAndHalfSelection();
                    if(isComboHalfAndHalf){
                      (toggleBottomSheet as () => void)();
                    }
                   }}>{t("common.close")}</button>}
                    <button 
                    className={`${styles.updateButton} ${isComboHalfAndHalf && invalidModifiers.length > 0 ? styles.disabled : ''}`}
                    onClick={() => {
                      if(isComboHalfAndHalf){
                      (toggleBottomSheet as () => void)();
                      }
                    }}
                    disabled={isComboHalfAndHalf && invalidModifiers.length > 0}
                    >
                    {t("common.update")} ({isComboHalfAndHalf? totalPriceInc :totalPrice})
                    </button>
                  </div>}
              </div>}
            </div>
          </>

          )}
        </Container>

        {showLeftPizzaSheet || showRightPizzaSheet? <div className={styles.customBackDrop} /> : <></>}

          <BottomSheet 
              show={showLeftPizzaSheet} 
              onHide={() => {
                // setShowLeftPizzaSheet(false);
                setLeftSideDetails(prev => {
                  // If there's no existing details, keep as is
                  if (!prev) return prev;
                  const sideMod = prev.sideModifier;
                  // Only update when sideModifier exists to preserve required Modifier fields
                  if (!sideMod) return prev;

                  const updatedSideModifier: Modifier = {
                    ...sideMod,
                    mofireOptions: (sideMod.mofireOptions || []).map((option: ModifierOption) => {
                      return { ...option, isApplied: false };
                    })
                  };

                  return {
                    ...prev,
                    sideModifier: updatedSideModifier
                  };
                });
              }} 
              title={t('ItemView.PICK_LEFT_SIDE_ITEM')}
          >

              <div className={styles.optionGrid}>

                  {leftSideDetails?.sideModifier?.mofireOptions.map((optionModifier: ModifierOption) => (
                    <React.Fragment key={optionModifier.optionId}>
                      {getSelectedOptionOfOtherSide("leftSideDetails").toLowerCase() !== optionModifier.optionName.toLowerCase() && (
                        <div style={{height:"100px"}} className={styles.optionCard + ' ' + (optionModifier.isSoldOut ? styles.soldOut : '')} onClick={() => handleSideModifierChange("leftSideDetails", "sideModifier", optionModifier)}>
                          <Form.Check
                              type="radio"
                              className={styles.checkmark}
                              checked={optionModifier.isApplied}
                              id={`left-side-modifier-${optionModifier.optionId}`}
                              onChange={() => {}}
                          />
                          <div className={styles.optionImage}>
                              <Image src={optionModifier?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png'} alt={optionModifier.optionDisplayName} width={32} height={102} />
                          </div>
                          <span className={styles.optionTitle}>{optionModifier.optionDisplayName}
                            <span className={styles.optionPrice}>
                              <div style={{ display: 'inline-flex' }}>
                                <span className={optionModifier.isSoldOut ? styles.soldOut : ''}>(+{formatCurrency((optionModifier?.optionPrice?.amount || 0), (optionModifier?.optionPrice?.currency || 'USD'))})</span>
                                {optionModifier.isSoldOut && <span className={styles.soldOutLabel}>{t('ItemView.SOLD_OUT')}</span>}
                              </div>
                            </span>
                          </span>
                      </div>
                      )}
                    </React.Fragment>
                  ))}
              </div>
              <div className={styles.buttonGroup}>
                  {/* <button className={`${styles.button} ${styles.secondary}`}>Previous</button> */}
                  <button id={`left-side-customization-close-button`} onClick={() => setShowLeftPizzaSheet(false)} className={`${styles.button} ${styles.primary}`}>{t('OpeningHoursModal.CLOSE')}</button>
              </div>
          </BottomSheet>

          {/* Right Pizza Customization Sheet */}
          <BottomSheet
              show={showRightPizzaSheet}
              onHide={() => {
                // setShowRightPizzaSheet(false);
                setRightSideDetails(prev => {
                  // If there's no existing details, keep as is
                  if (!prev) return prev;
                  const sideMod = prev.sideModifier;
                  // Only update when sideModifier exists to preserve required Modifier fields
                  if (!sideMod) return prev;
                  const updatedSideModifier: Modifier = {
                    ...sideMod,
                    mofireOptions: (sideMod.mofireOptions || []).map((option: ModifierOption) => {
                      return { ...option, isApplied: false };
                    })
                  };
                  return {
                    ...prev,
                    sideModifier: updatedSideModifier
                  };
                });
              }}
              title={t('ItemView.PICK_RIGHT_SIDE_ITEM')}
          >
              <div className={styles.optionGrid}>
                  {rightSideDetails?.sideModifier?.mofireOptions.map((optionModifier: ModifierOption) => (
                    <React.Fragment key={optionModifier.optionId}>

                      {getSelectedOptionOfOtherSide("rightSideDetails").toLowerCase() !== optionModifier.optionName.toLowerCase() && (
                        <div className={styles.optionCard + ' ' + (optionModifier.isSoldOut ? styles.soldOut : '')} onClick={() => handleSideModifierChange("rightSideDetails", "sideModifier", optionModifier)}>
                          <Form.Check
                              type="radio"
                              className={styles.checkmark}
                              checked={optionModifier.isApplied}
                              id={`right-side-modifier-${optionModifier.optionId}`}
                              onChange={() => {}}
                              />
                              
                          <div className={styles.optionImage}>
                              <Image src={optionModifier?.optionImageUrl?.imageUrl || '/assets/images/placeholderActualSize.png'} alt={optionModifier.optionDisplayName} width={32} height={102}
                               onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/placeholder.png'; }}
                              />
                          </div>

                          <span className={styles.optionTitle}>{optionModifier.optionDisplayName}
                            <span className={styles.optionPrice}>
                              <div style={{ display: 'inline-flex' }}>
                                <span className={optionModifier.isSoldOut ? styles.soldOut : ''}>(+{formatCurrency((optionModifier?.optionPrice?.amount || 0), (optionModifier?.optionPrice?.currency || 'USD'))})</span>
                                {optionModifier.isSoldOut && <span className={styles.soldOutLabel}>{t('ItemView.SOLD_OUT')}</span>}
                              </div>
                            </span>
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
              </div>
              <div className={styles.buttonGroup}>
                  {/* <button className={`${styles.button} ${styles.secondary}`}>Previous</button> */}
                  <button id={`right-side-customization-close-button`} onClick={() => setShowRightPizzaSheet(false)} className={`${styles.button} ${styles.primary}`}>
                    {t('OpeningHoursModal.CLOSE')}
                  </button>
              </div>
          </BottomSheet>

      </main>

      {/* Customize Bottom Sheet */}
      {!selectedItem?.isSoldOut && <CustomizeSelector
        isHalfAndHalf={isHalfAndHalf}
        isBottomSheet={isBottomSheet}
        isShowDietaryOptions={false}
        showCustomize={showCustomize}
        ItemNameForAlert={saveOrderErrorQueue.length > 0 ?(isHalfAndHalf ? (customizeSide === 'leftSideDetails' ? leftSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.optionDisplayName : rightSideDetails?.sideModifier?.mofireOptions.find((optionItem: ModifierOption) => optionItem.isApplied)?.optionDisplayName ) : (selectedItem?.itemName) ): undefined}
        setPauseNextStep={setPauseNextStep}
        showingTabId={showingTabId}
        currentStepCount={currentSaveOrderErrorIndex + 1}
        fullStepCount={validErrorCount}
        onAddToCart={saveOrder} 
        modifiers={isHalfAndHalf ? (customizeSide === 'leftSideDetails' ? leftSideDetails?.modifiers : rightSideDetails?.modifiers) : (selectedItem?.modifiers || [])}
        totalPrice={isComboHalfAndHalf? totalPriceInc :totalPrice}
        setShowCustomize={setShowCustomize}
        setModifiers={isComboHalfAndHalf && setModifiers ? setModifiers :handleSetModifiers}
        handleModifierChange={isComboHalfAndHalf && onModifierChange ? onModifierChange : handleModifierChange}
        resetModifiers={handleResetModifiers}
      />
      }
      {/* Cart Success Bottom Sheet */}
      <ModalWrapperSheet
        show={showSuccessSheet}
        onClose={() => setShowSuccessSheet(false)}
      >
        <div className={cartsuccessstyles.illustration}>
          <Lottie animationData={itemAdded} loop={false} />
        </div>

        <div className={cartsuccessstyles.textContent}>
          <h2 className={cartsuccessstyles.title}>{t('CartSuccessSheet.ITEM_ADDED')}</h2>
          <p className={cartsuccessstyles.description}>
            {t('CartSuccessSheet.ITEM_ADDED_DESCRIPTION')}
          </p>
        </div>

        <div className={cartsuccessstyles.buttons}>
            <Link href={"/cart"+cartId} id="success-view-cart-link" className={cartsuccessstyles.viewCart}>
                <button className={cartsuccessstyles.viewCart} onClick={() => {
                  setShowSuccessSheet(false);
                  cookingLoadingService.loading(true);
                  router.push('/cart' + cartId);
                }}>
                    {t('CartSuccessSheet.VIEW_CART')}
                </button>
            </Link>
            <Link href="/menu/all" id="success-continue-shopping-link" className={cartsuccessstyles.continueShopping}>
                <button className={cartsuccessstyles.continueShopping} onClick={(e) => {
                  e.preventDefault();
                  setShowSuccessSheet(false);

                  if(isBottomSheet) {
                    (toggleBottomSheet as () => void)();
                  } else{
                    router.push('/menu/all');
                  }
                }}>
                    {t('CartSuccessSheet.CONTINUE_BROWSING')}
                </button>
            </Link>
        </div>
      </ModalWrapperSheet>

      {/* Out of stock overlay */}
      {selectedItem?.isSoldOut && <ModalWrapperSheet 
        show={showOutOfStockSheet}
        onClose={() => setShowOutOfStockSheet(false)}
      >
        <div className={`${cartsuccessstyles.illustration} ${cartsuccessstyles.outOfStockIllustration}`}>
          <Image src={'/assets/images/order-canceled.png'} alt="Item Sold Out" width={150} height={130} />
        </div>

        <div className={cartsuccessstyles.textContent}>
          <h2 className={cartsuccessstyles.title}>{t('ItemView.ITEM_SOLD_OUT')}</h2>
          <p className={cartsuccessstyles.description}>
            {t('ItemView.SOLD_OUT_MESSAGE')}
          </p>
        </div>

        <div className={cartsuccessstyles.buttons}>
            <button 
              id="out-of-stock-continue-shopping-button" 
              className={`${cartsuccessstyles.viewCart}`} 
              onClick={() => {
                if(isBottomSheet) {
                  (toggleBottomSheet as () => void)();
                } else{
                  router.push('/menu/all');
                }
              }}>
              {t('ItemView.TRY_SOMETHING_NEW')}
            </button>

            <button 
              id="out-of-stock-close-button" 
              className={cartsuccessstyles.addToCart} 
              onClick={() => {
                setShowOutOfStockSheet(false);
              }}>
              {t('ItemView.CLOSE')}
            </button>

        </div>
      </ModalWrapperSheet>}
      <RemoveCustomizeConfirmation
        show={showRemoveCustomizeConfirmation}
        onClose={() => setShowRemoveCustomizeConfirmation(false)}
        onConfirm={() => {
          // updateSelection();
          setShowRemoveCustomizeConfirmation(false);
          setShowCustomize(false);
        }}
      />

    </RemoveScroll>
  );
}